import {
  Box,
  Button,
  Chip,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  FormControl,
  Checkbox,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Switch,
  IconButton,
  Tooltip,
  Divider,
  Autocomplete,
  Avatar,
  Grid,
  CircularProgress,
} from "@mui/material";
import { useState, useCallback, useEffect, useMemo } from "react";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import ToggleOnRoundedIcon from "@mui/icons-material/ToggleOnRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import {
  useSubmitViewDBFiltersMutation,
  useGetdbfildsFilterQuery,
  useGetsearchdatalistQuery,
  useDeleteSearchDataMutation,
  useGetcountsMutation,
  useUploadedDataCountsQuery,
  useGetusagehistorybyidQuery,
  useCreateEventFlowMutation,
  useGetsuggetionlistQuery,
  useLazyGetsuggetionlistQuery,
  useUpdatesearchdatalistMutation,
  useCancelSearchDataMutation,
} from "../store/services/createDbApi";
import { toast } from "react-toastify";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { layoutCSS } from "../component/common/commonStyle";
import {
  useGetMissedCallNumbersQuery,
  useGetShortcodesQuery,
  useGetMissedCallFilterReportQuery,
  useGetShortcodeFilterReportQuery,
  useGetShortcodeKeywordsQuery,
  useGetShortcodeSubKeywordsQuery,
  useSaveFilterReportMutation,
} from "../store/services/missedCallApi";
import { useGetTemplatesQuery } from "../store/services/whatsappApi";
import { useGetRcsTemplateListQuery, useGetRcsTemplateViewQuery } from "../store/services/rcstemplateApi";
import { useGetFlowsQuery } from "../store/services/flowApi";
import { useGetTemplateByPrincipalIdQuery } from "../store/services/templateApi";
import { jwtDecode } from "jwt-decode";

// ── Shared style tokens ────────────────────────────────────────────────────
const glassInput = {
  background: "rgba(255,255,255,0.7)",
  backdropFilter: "blur(8px)",
  borderRadius: "8px",
};

const smallSelectSx = {
  fontSize: "0.75rem",
  ...glassInput,
  "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.05)" },
};

const dateFieldSx = {
  width: 140,
  "& .MuiInputBase-input": { fontSize: "0.75rem", padding: "6px 8px" },
  "& .MuiInputLabel-root": { fontSize: "0.75rem" },
  "& .MuiOutlinedInput-root": { ...glassInput },
};

const filterNameFieldSx = {
  "& .MuiInputBase-input": { padding: "4px 6px", fontSize: "0.75rem" },
  "& .MuiOutlinedInput-root": { height: "32px", ...glassInput },
  width: "160px",
};

const chipAreaSx = {
  flexWrap: "wrap",
  gap: 1.2,
  p: 1.5,
  border: "1px dashed #ccc",
  borderRadius: "12px",
  background: "rgba(255,255,255,0.5)",
  backdropFilter: "blur(6px)",
  minHeight: 40,
};

// ── Table header cell (matches WorkFlow component) ────────────────────────
const thCell = {
  fontSize: "0.82rem",
  fontWeight: 600,
  color: "white",
  borderRight: "0.5px solid #3a3a3a",
  py: 1,
};

// ── Table body cell ───────────────────────────────────────────────────────
const tdCell = {
  padding: "8px",
  fontSize: "0.8rem",
  borderRight: "0.5px solid #d0d0d0",
};

// ── Select Flow dialog design tokens (ported from EventCreation) ──────────
const BRAND = "#0288d1";

const dialogPaperSx = {
  borderRadius: "16px",
  boxShadow: "0 24px 60px rgba(2, 136, 209, 0.18), 0 4px 12px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const dialogHeaderSx = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  px: 3,
  py: 2,
  background: `linear-gradient(135deg, ${BRAND} 0%, #01579b 100%)`,
  color: "#fff",
};

const dialogSectionSx = {
  border: "1px solid #E6ECF1",
  borderRadius: "12px",
  p: 2,
  background: "#FAFCFE",
};

const sectionLabelSx = {
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  fontSize: "0.78rem",
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "#4A5A68",
  mb: 1.5,
};

const FALLBACK_TIMEZONES = [
  "Asia/Kolkata",
  "UTC",
  "Asia/Dubai",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Europe/London",
  "Europe/Berlin",
  "America/New_York",
  "America/Chicago",
  "America/Los_Angeles",
  "Australia/Sydney",
];

const SERVICE_OPTIONS = [
  { value: "flow", label: "Flow" },
  { value: "sms", label: "SMS" },
  { value: "rcs", label: "RCS" },
  { value: "wa", label: "WhatsApp (WA)" },
];

const RUN_DURATION_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "monthly", label: "Monthly" },
  { value: "half_yearly", label: "Half Yearly" },
  { value: "yearly", label: "Yearly" },
  { value: "custom", label: "Custom" },
];

// Shown when Run Duration = "Custom": run the schedule by a fixed weekday
// pattern (reuses the weekday selector) or by explicit calendar dates.
const CUSTOM_SCHEDULE_BY_OPTIONS = [
  { value: "day", label: "Day wise" },
  { value: "date", label: "Date wise" },
];

const MAX_CUSTOM_DAYS = 365;

// Campaign type: how the flow should keep running once triggered.
const CAMPAIGN_TYPE_OPTIONS = [
  { value: "onetime", label: "Onetime" },
  { value: "realtime", label: "Realtime" },
  { value: "continues", label: "Continues" },
];

// Days available for "Daily" schedules that run only on specific weekdays.
const WEEKDAY_OPTIONS = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

// Maps internal serviceType values to the exact strings the createEventFlow
// API expects for "service_type" (per payload contract: "Flow", "SMS", "RCS", "WA").
const SERVICE_TYPE_LABELS = {
  flow: "Flow",
  sms: "SMS",
  rcs: "RCS",
  wa: "WA",
};

// Best-effort conversion of a "YYYY-MM-DDTHH:mm" local wall-clock string + IANA
// timezone into an ISO 8601 string with that timezone's UTC offset, e.g.
// "2026-08-13T10:30:00+05:30". Uses one pass of Intl.DateTimeFormat to read the
// offset, which is accurate for almost all real-world cases (offset changes
// mid-selection due to DST transitions are a rare edge case).
const buildNextRunAtISO = (localDateTime, timeZone) => {
  if (!localDateTime) return "";
  const withSeconds = localDateTime.length === 16 ? `${localDateTime}:00` : localDateTime;

  try {
    const approxDate = new Date(localDateTime);
    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "shortOffset",
    });
    const parts = dtf.formatToParts(approxDate);
    const tzPart = parts.find((p) => p.type === "timeZoneName")?.value || "GMT+00:00";
    const match = tzPart.match(/GMT([+-])(\d{1,2})(?::?(\d{2}))?/);
    if (!match) return `${withSeconds}+00:00`;
    const sign = match[1];
    const hours = match[2].padStart(2, "0");
    const minutes = (match[3] || "00").padStart(2, "0");
    return `${withSeconds}${sign}${hours}:${minutes}`;
  } catch {
    return `${withSeconds}+00:00`;
  }
};

// ── Template preview: multi-card carousel ──────────────────────────────
function TemplateCardCarousel({ cards }) {
  const [index, setIndex] = useState(0);
  const total = cards.length;

  const goTo = (i) => setIndex(((i % total) + total) % total);

  const card = cards[index];
  const cardHeader = card?.components?.find((c) => c.type === "HEADER");
  const cardBody = card?.components?.find((c) => c.type === "BODY");
  const cardButtons = card?.components?.find((c) => c.type === "BUTTONS");
  const cardMedia = cardHeader?.example?.header_handle?.[0];

  return (
    <Box>
      <Box
        sx={{
          position: "relative",
          border: "1px solid #E6ECF1",
          borderRadius: 3,
          bgcolor: "#FAFCFE",
          overflow: "hidden",
        }}
      >
        {/* Card counter */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            pt: 1.5,
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: "0.82rem", color: BRAND }}>
            Card {index + 1} of {total}
          </Typography>
          <Stack direction="row" spacing={0.6}>
            {cards.map((_, i) => (
              <Box
                key={i}
                onClick={() => goTo(i)}
                sx={{
                  width: i === index ? 16 : 6,
                  height: 6,
                  borderRadius: 3,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  background: i === index ? BRAND : "#C7D6E0",
                }}
              />
            ))}
          </Stack>
        </Box>

        {/* Card content */}
        <Box sx={{ p: 2, pt: 1.5 }}>
          {cardMedia && (
            <Box sx={{ mb: 1.5, borderRadius: 2, overflow: "hidden" }}>
              <img
                src={cardMedia}
                alt={`card-${index + 1}`}
                style={{ width: "100%", maxHeight: 200, objectFit: "cover", display: "block" }}
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.parentNode.innerHTML =
                    '<div style="padding: 20px; text-align: center; background: #f0f0f0;">Media not available</div>';
                }}
              />
            </Box>
          )}
          {cardBody?.text && (
            <Typography sx={{ fontSize: "0.88rem", mb: 1, minHeight: "3em" }}>{cardBody.text}</Typography>
          )}
          {cardButtons?.buttons && cardButtons.buttons.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {cardButtons.buttons.map((btn, btnIndex) => (
                <Chip
                  key={btnIndex}
                  label={`${btn.text || btn.type}${btn.url ? ` (${btn.url})` : ""}`}
                  size="small"
                  sx={{ mr: 1, mb: 1, borderRadius: "8px" }}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Prev / Next arrows */}
        {total > 1 && (
          <>
            <IconButton
              onClick={() => goTo(index - 1)}
              size="small"
              sx={{
                position: "absolute",
                top: "50%",
                left: 6,
                transform: "translateY(-50%)",
                bgcolor: "rgba(255,255,255,0.9)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                "&:hover": { bgcolor: "#fff" },
              }}
            >
              <ArrowBackIosNewRoundedIcon sx={{ fontSize: "0.85rem" }} />
            </IconButton>
            <IconButton
              onClick={() => goTo(index + 1)}
              size="small"
              sx={{
                position: "absolute",
                top: "50%",
                right: 6,
                transform: "translateY(-50%)",
                bgcolor: "rgba(255,255,255,0.9)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                "&:hover": { bgcolor: "#fff" },
              }}
            >
              <ArrowForwardIosRoundedIcon sx={{ fontSize: "0.85rem" }} />
            </IconButton>
          </>
        )}
      </Box>

      {/* Thumbnail strip for quick jump */}
      {total > 1 && (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5, overflowX: "auto", pb: 0.5 }}>
          {cards.map((c, i) => {
            const h = c?.components?.find((cc) => cc.type === "HEADER");
            const thumb = h?.example?.header_handle?.[0];
            return (
              <Box
                key={i}
                onClick={() => goTo(i)}
                sx={{
                  flex: "0 0 auto",
                  width: 48,
                  height: 48,
                  borderRadius: 1.5,
                  overflow: "hidden",
                  cursor: "pointer",
                  border: i === index ? `2px solid ${BRAND}` : "2px solid transparent",
                  opacity: i === index ? 1 : 0.6,
                  transition: "all 0.15s ease",
                  bgcolor: "#EEF3F7",
                }}
              >
                {thumb ? (
                  <img
                    src={thumb}
                    alt={`thumb-${i + 1}`}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                ) : null}
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}

const getErrorMessage = (err, fallback = "Something went wrong. Please try again.") => {
  if (!err) return fallback;
  if (typeof err.data === "string" && err.data.trim()) return err.data;
  if (err.data?.message) return err.data.message;
  if (err.data?.error) return err.data.error;
  if (err.error) return err.error;
  if (err.message) return err.message;
  return fallback;
};
// ──────────────────────────────────────────────────────────────────────────

function ViewDataFields() {
  const [filterType, setFilterType] = useState("DB");
  const [selected, setSelected] = useState("");
  const [chips, setChips] = useState([]);

  const [selectedFields, setSelectedFields] = useState([""]);
  const [dataTypes, setDataTypes] = useState([""]);
  const [operations, setOperations] = useState([""]);
  const [values, setValues] = useState([""]);
  const [logicalOperators, setLogicalOperators] = useState(["AND"]);
  const today = new Date().toISOString().split("T")[0];

  const [missedCallDateFrom, setMissedCallDateFrom] = useState(today);
  const [missedCallDateTill, setMissedCallDateTill] = useState(today);
  const [selectedMissedCallNumbers, setSelectedMissedCallNumbers] = useState([]);
  const [callerNumbers, setCallerNumbers] = useState([]);

  const [sortcodeDateFrom, setSortcodeDateFrom] = useState(today);
  const [sortcodeDateTill, setSortcodeDateTill] = useState(today);
  const [selectedShortcodes, setSelectedShortcodes] = useState([]);
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [selectedSubKeywords, setSelectedSubKeywords] = useState([]);
  const [mobileNumbers, setMobileNumbers] = useState([]);

  // Autocomplete suggestions per value-input row
  const [suggestionsMap, setSuggestionsMap] = useState({});
  const [triggerSuggest, { isFetching: isSuggesting }] = useLazyGetsuggetionlistQuery();

  const handleSuggestionSelect = (index, val) => {
    const v = [...values];
    v[index] = val;
    setValues(v);
    setSuggestionsMap((p) => ({ ...p, [index]: [] }));
  };

  const handleValueInputChange = async (index, val) => {
    const v = [...values];
    v[index] = val;
    setValues(v);

    const fieldName = selectedFields[index];
    if (!fieldName || !val || val.trim().length === 0) {
      setSuggestionsMap((p) => ({ ...p, [index]: [] }));
      return;
    }

    try {
      const res = await triggerSuggest(fieldName).unwrap();
      const list = (res && res.data) || res || [];
      setSuggestionsMap((p) => ({ ...p, [index]: Array.isArray(list) ? list : [] }));
    } catch (err) {
      setSuggestionsMap((p) => ({ ...p, [index]: [] }));
    }
  };

  // ── Sequence chips for Missed Call and Shortcode ────────────────────────
  const [missedCallChips, setMissedCallChips] = useState([]);
  const [shortcodeChips, setShortcodeChips] = useState([]);
  const [selectedMissedCallField, setSelectedMissedCallField] = useState("");
  const [selectedShortcodeField, setSelectedShortcodeField] = useState("");

  // ── Placeholder field lists (will be replaced by API calls) ─────────────
  // TODO: Replace with actual API data when available
  const missedCallFields = [
    { value: "logdate", label: "Create Date" },
    { value: "caller_number", label: "Ph No" },
    { value: "virtual_number", label: "Missedcall Number" },
  ];

  const shortcodeFields = [
    { value: "mobile", label: "Ph No" },
    { value: "shortcode", label: "Shortcode" },
    { value: "keyword", label: "Keyword" },
    { value: "logdate", label: "Create Date" },
   
  ];

  const getMissedCallFieldLabel = (field) => missedCallFields.find((item) => item.value === field)?.label || field;
  const getShortcodeFieldLabel = (field) => shortcodeFields.find((item) => item.value === field)?.label || field;

  // ── Submit dialog: partial data / split data toggles ───────────────────
  const [givePartialData, setGivePartialData] = useState(false);
  const [splitData, setSplitData] = useState(false);

  const [filterName, setFilterName] = useState("");
  const [recordsPerPage, setRecordsPerPage] = useState(0);
  const [batchLimit, setBatchLimit] = useState(0);
  const [openSubmitDialog, setOpenSubmitDialog] = useState(false);

  // ── Get Count popup state ───────────────────────────────────────────────
  const [countResult, setCountResult] = useState(null);

  // ── Submitted filters list (table rendered below "Get Count") ──────────
  const [dataListPage, setDataListPage] = useState(1);
  const [dataListLimit] = useState(10);

  // ── Delete confirmation state ───────────────────────────────────────────
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  // ── Update filter dialog state (Action → Edit) ───────────────────────────
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editTargetItem, setEditTargetItem] = useState(null);
  const [editFilterName, setEditFilterName] = useState("");
  const [editSelected, setEditSelected] = useState("");
  const [editChips, setEditChips] = useState([]);
  const [editSelectedFields, setEditSelectedFields] = useState([""]);
  const [editDataTypes, setEditDataTypes] = useState([""]);
  const [editOperations, setEditOperations] = useState([""]);
  const [editValues, setEditValues] = useState([""]);
  const [editLogicalOperators, setEditLogicalOperators] = useState(["AND"]);

  // ── Cancel filter confirmation state (Action → Cancel) ──────────────────
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [cancelTargetItem, setCancelTargetItem] = useState(null);

  // ── Info (SMS / Clicker history) popup state ────────────────────────────
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [infoDialogItem, setInfoDialogItem] = useState(null);
  const [usageHistoryId, setUsageHistoryId] = useState(null);

  // ── Select Flow popup state (ported from EventCreation) ─────────────────
  const [flowDialogOpen, setFlowDialogOpen] = useState(false);
  const [flowDialogItem, setFlowDialogItem] = useState(null);
  const [serviceType, setServiceType] = useState("");
  const [selectedFlow, setSelectedFlow] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [runDuration, setRunDuration] = useState("");
  const [runTime, setRunTime] = useState("");
  const [timeZone, setTimeZone] = useState("Asia/Kolkata");
  const [nextRunAt, setNextRunAt] = useState("");
  const [eventStatus, setEventStatus] = useState("paused"); // default Paused
  const [selectedDays, setSelectedDays] = useState([]); // used for "daily" schedule_type
  const [customerId, setCustomerId] = useState(null);

  // ── Custom run duration state (Run Duration = "custom") ─────────────────
  const [customNoOfDays, setCustomNoOfDays] = useState("");
  const [customScheduleBy, setCustomScheduleBy] = useState("day"); // "day" | "date"
  const [customDates, setCustomDates] = useState([]);
  const [customDateInput, setCustomDateInput] = useState("");
  const [campaignType, setCampaignType] = useState(""); // onetime | realtime | continues

  const { data: getdbfilterfield } = useGetdbfildsFilterQuery();
  const { data: missedCallNumbersData } = useGetMissedCallNumbersQuery();
  const { data: shortcodesData } = useGetShortcodesQuery();
  const [submitFilters] = useSubmitViewDBFiltersMutation();
  const [saveFilterReport] = useSaveFilterReportMutation();
  const [deleteSearchData, { isLoading: isDeleting }] = useDeleteSearchDataMutation();
  const [submitpayload, { isLoading: isCountLoading }] = useGetcountsMutation();
  const { data: uploadedDataCounts } = useUploadedDataCountsQuery();
  const [updateSearchData, { isLoading: isUpdatingFilter }] = useUpdatesearchdatalistMutation();
  const [cancelSearchData, { isLoading: isCancellingFilter }] = useCancelSearchDataMutation();

  // ── Select Flow: customer id from JWT (matches EventCreation) ──────────
  useEffect(() => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) return;
      const decoded = jwtDecode(token);
      const id = decoded?.customer_id || decoded?.id || decoded?.userid || null;
      if (id) setCustomerId(id);
    } catch (err) {
      // ignore invalid token in this view
    }
  }, []);

  // Create event flow (Select Flow dialog save) mutation
  const [createEventFlow, { isLoading: isSavingFlow }] = useCreateEventFlowMutation();

  // Fetch WA templates when WA is selected and customerId available
  const { data: waTemplateData = {}, isLoading: waTemplatesLoading } = useGetTemplatesQuery(
    { customer_id: customerId },
    { skip: !customerId || serviceType !== "wa" }
  );

  const waTemplates = (waTemplateData?.templates ?? [])
    .filter((t) => t.status?.toUpperCase() === "APPROVED")
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  // RCS templates (fetch when RCS selected)
  const { data: rcsResp = {}, isLoading: rcsLoading } = useGetRcsTemplateListQuery(
    { id: customerId, page: 1, limit: 100 },
    { skip: !customerId || serviceType !== "rcs" }
  );

  const rcsTemplates = (rcsResp?.data ?? [])
    .map((t) => ({ id: t.template_id || t.rcs_template_id || t.id, name: t.template_name }))
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  // Flows (fetch when Flow selected)
  const { data: flowsResp = {}, isLoading: flowsLoading } = useGetFlowsQuery(
    { page: 1, search: "" },
    { skip: serviceType !== "flow" }
  );

  const fetchedFlows = (flowsResp?.data ?? []).map((f) => ({ flow_id: f.cf_id || f.flow_id, flow_name: f.cf_name || f.flow_name }));

  // SMS templates for principal (fetch when SMS selected)
  const { data: smsResp = {}, isLoading: smsLoading } = useGetTemplateByPrincipalIdQuery(
    customerId,
    { skip: !customerId || serviceType !== "sms" }
  );

  const smsTemplates = (smsResp?.data ?? []).map((t) => ({ template_id: t.template_id || t.id, template_name: t.template_name || t.name }));

  // RCS template view for preview
  const { data: rcsTemplateView } = useGetRcsTemplateViewQuery(selectedTemplate, { skip: !selectedTemplate || serviceType !== "rcs" });

  const timeZones = useMemo(() => {
    try {
      const zones = Intl.supportedValuesOf("timeZone");
      return zones?.length ? zones : FALLBACK_TIMEZONES;
    } catch {
      return FALLBACK_TIMEZONES;
    }
  }, []);

  const {
    data: usageHistoryData,
    isLoading: usageHistoryLoading,
    isError: usageHistoryError,
  } = useGetusagehistorybyidQuery(usageHistoryId, {
    skip: !usageHistoryId,
    refetchOnMountOrArgChange: true,
  });
  console.log("uploadedDataCounts", uploadedDataCounts);

  const {
    data: searchDataList,
    isLoading: isSearchDataListLoading,
    refetch: refetchSearchDataList,
  } = useGetsearchdatalistQuery({ page: dataListPage, limit: dataListLimit });

  const { data: keywordsData, refetch: fetchKeywords } = useGetShortcodeKeywordsQuery(
    { shortcode: selectedShortcodes.join(","), start_date: sortcodeDateFrom, end_date: sortcodeDateTill },
    { skip: selectedShortcodes.length === 0 || !sortcodeDateFrom || !sortcodeDateTill }
  );

  const { data: subKeywordsData, refetch: fetchSubKeywords } = useGetShortcodeSubKeywordsQuery(
    {
      shortcode: selectedShortcodes.join(","),
      keyword: selectedKeywords.join(","),
      start_date: sortcodeDateFrom,
      end_date: sortcodeDateTill,
    },
    {
      skip: selectedShortcodes.length === 0 || selectedKeywords.length === 0 || !sortcodeDateFrom || !sortcodeDateTill,
    }
  );

  const { data: missedCallReportData, refetch: fetchMissedCallReport } =
    useGetMissedCallFilterReportQuery(
      {
        start_date: missedCallDateFrom,
        end_date: missedCallDateTill,
        missedcall_numberid: selectedMissedCallNumbers.join(","),
        page: 1,
        limit: 1000,
      },
      { skip: !missedCallDateFrom || !missedCallDateTill || selectedMissedCallNumbers.length === 0 }
    );

  const { data: shortcodeReportData, refetch: fetchShortcodeReport } =
    useGetShortcodeFilterReportQuery(
      {
        start_date: sortcodeDateFrom,
        end_date: sortcodeDateTill,
        shortcode: selectedShortcodes.join(","),
        keyword: selectedKeywords.join(","),
        subkeyword: selectedSubKeywords.join(","),
        page: 1,
        limit: 1000,
      },
      { skip: !sortcodeDateFrom || !sortcodeDateTill || selectedShortcodes.length === 0 }
    );

  const rawData = getdbfilterfield?.data || [];
  const columns = rawData.flat();
  const fieldList = columns.map((c) => c.column_name);
  const missedCallNumbers = missedCallNumbersData?.data || [];
  const shortcodes = shortcodesData?.data || [];

  const availableKeywords = keywordsData?.data
    ? [...new Set(keywordsData.data.map((i) => i.keyword).filter(Boolean))]
    : [];
  const availableSubKeywords = subKeywordsData?.data
    ? [...new Set(subKeywordsData.data.map((i) => i.subkeyword).filter(Boolean))]
    : [];

  useEffect(() => {
    if (selectedShortcodes.length > 0 && sortcodeDateFrom && sortcodeDateTill) {
      fetchKeywords();
      setSelectedKeywords([]);
      setSelectedSubKeywords([]);
      setMobileNumbers([]);
    }
  }, [selectedShortcodes, sortcodeDateFrom, sortcodeDateTill, fetchKeywords]);

  useEffect(() => {
    if (selectedKeywords.length > 0 && selectedShortcodes.length > 0 && sortcodeDateFrom && sortcodeDateTill) {
      fetchSubKeywords();
      setSelectedSubKeywords([]);
      setMobileNumbers([]);
    }
  }, [selectedKeywords, selectedShortcodes, sortcodeDateFrom, sortcodeDateTill, fetchSubKeywords]);

  useEffect(() => {
    const run = async () => {
      if (selectedSubKeywords.length > 0 && selectedShortcodes.length > 0 && sortcodeDateFrom && sortcodeDateTill) {
        try {
          const result = await fetchShortcodeReport();
          if (result.data?.success) {
            const nums = [...new Set(result.data.data.map((i) => i.mobile))];
            setMobileNumbers(nums.filter((n) => n && n.trim() !== ""));
          } else setMobileNumbers([]);
        } catch { setMobileNumbers([]); }
      } else setMobileNumbers([]);
    };
    run();
  }, [selectedSubKeywords, selectedShortcodes, selectedKeywords, sortcodeDateFrom, sortcodeDateTill, fetchShortcodeReport]);

  const handleSelect = (e) => {
    const v = e.target.value;
    if (!chips.includes(v)) {
      if (v === "ph_no") {
        setChips((p) => [v, ...p]);
      } else {
        setChips((p) => [...p, v]);
      }
    }
    setSelected("");
  };

  // ── Missed Call chip handlers ───────────────────────────────────────────
  const handleMissedCallFieldSelect = (e) => {
    const v = e.target.value;
    if (!missedCallChips.includes(v)) {
      setMissedCallChips((p) =>
        v === "caller_number" ? [v, ...p.filter((item) => item !== v)] : [...p, v]
      );
    }
    setSelectedMissedCallField("");
  };

  const handleMissedCallChipDelete = (chip) => {
    setMissedCallChips((p) => p.filter((x) => x !== chip));
  };

  const onMissedCallDragEnd = useCallback((result) => {
    if (!result.destination) return;
    const r = Array.from(missedCallChips);
    const [m] = r.splice(result.source.index, 1);
    if (m === "caller_number") {
      r.unshift(m);
    } else {
      const destIndex = result.destination.index === 0 && r[0] === "caller_number" ? 1 : result.destination.index;
      r.splice(destIndex, 0, m);
    }
    setMissedCallChips(r);
  }, [missedCallChips]);

  // ── Shortcode chip handlers ─────────────────────────────────────────────
  const handleShortcodeFieldSelect = (e) => {
    const v = e.target.value;
    if (!shortcodeChips.includes(v)) {
      setShortcodeChips((p) =>
        v === "mobile" ? [v, ...p.filter((item) => item !== v)] : [...p, v]
      );
    }
    setSelectedShortcodeField("");
  };

  const handleShortcodeChipDelete = (chip) => {
    setShortcodeChips((p) => p.filter((x) => x !== chip));
  };

  const onShortcodeDragEnd = useCallback((result) => {
    if (!result.destination) return;
    const r = Array.from(shortcodeChips);
    const [m] = r.splice(result.source.index, 1);
    if (m === "mobile") {
      r.unshift(m);
    } else {
      const destIndex = result.destination.index === 0 && r[0] === "mobile" ? 1 : result.destination.index;
      r.splice(destIndex, 0, m);
    }
    setShortcodeChips(r);
  }, [shortcodeChips]);

  const handleShortcodeChange = (e) => setSelectedShortcodes(e.target.value);
  const handleKeywordChange = (e) => setSelectedKeywords(e.target.value);
  const handleSubKeywordChange = (e) => setSelectedSubKeywords(e.target.value);
  const handleDelete = (c) => setChips((p) => p.filter((x) => x !== c));

  const onDragEnd = useCallback((result) => {
    if (!result.destination) return;
    const r = Array.from(chips);
    const [m] = r.splice(result.source.index, 1);

    if (m === "ph_no") {
      r.unshift(m);
    } else {
      const destIndex = result.destination.index === 0 && r[0] === "ph_no" ? 1 : result.destination.index;
      r.splice(destIndex, 0, m);
    }

    setChips(r);
  }, [chips]);

  const handleFieldChange = (index, fieldName) => {
    const f = [...selectedFields], d = [...dataTypes], o = [...operations], v = [...values];
    const col = columns.find((c) => c.column_name === fieldName);
    f[index] = fieldName; d[index] = col?.data_type || ""; o[index] = ""; v[index] = "";
    setSelectedFields(f); setDataTypes(d); setOperations(o); setValues(v);
  };

  const handleOperationChange = (index, op) => {
    const o = [...operations], v = [...values];
    o[index] = op; v[index] = "";
    setOperations(o); setValues(v);
  };

  const handleValueChange = (index, val) => {
    const v = [...values]; v[index] = val; setValues(v);
  };

  const handleLogicalOperatorChange = (index, operator) => {
    const next = [...logicalOperators];
    next[index] = operator;
    setLogicalOperators(next);
  };

  const handleAddRow = () => {
    if (selectedFields.length < fieldList.length) {
      setSelectedFields([...selectedFields, ""]); setDataTypes([...dataTypes, ""]);
      setOperations([...operations, ""]); setValues([...values, ""]);
      setLogicalOperators([...logicalOperators, "AND"]);
    }
  };

  const handleRemoveRow = (index) => {
    if (selectedFields.length > 1) {
      setSelectedFields(selectedFields.filter((_, i) => i !== index));
      setDataTypes(dataTypes.filter((_, i) => i !== index));
      setOperations(operations.filter((_, i) => i !== index));
      setValues(values.filter((_, i) => i !== index));
      setLogicalOperators(logicalOperators.filter((_, i) => i !== index));
    }
  };

  const handleMissedCallNumberChange = (e) => setSelectedMissedCallNumbers(e.target.value);

  useEffect(() => {
    const run = async () => {
      if (selectedMissedCallNumbers.length > 0 && missedCallDateFrom && missedCallDateTill) {
        try {
          const result = await fetchMissedCallReport();
          if (result.data?.success) {
            const nums = [...new Set(result.data.data.map((i) => i.caller_number))];
            setCallerNumbers(nums.filter((n) => n && n.trim() !== ""));
          } else setCallerNumbers([]);
        } catch { setCallerNumbers([]); }
      } else setCallerNumbers([]);
    };
    run();
  }, [selectedMissedCallNumbers, missedCallDateFrom, missedCallDateTill, fetchMissedCallReport]);

  const buildCountPayload = () => {
    if (filterType === "DB") {
      const jsonData = selectedFields
        .map((field, i) => ({
          fieldName: field,
          searchType: operations[i],
          value: serializeFilterValue(operations[i], values[i]),
          logicalOperator: logicalOperators[i] || "AND",
        }))
        .filter((item) => item.fieldName && item.searchType && item.value !== "");
      return {
        filtername: filterName,
        requiredData: chips.join(","),
        filterType: "db",
        jsonData,
      };
    } else if (filterType === "Missedcall Response") {
      return {
        filtername: filterName,
        filterType: "missedcall",
        requiredData: missedCallChips.join(","),
        dateFrom: missedCallDateFrom,
        dateTill: missedCallDateTill,
        missedCallNumbers: selectedMissedCallNumbers.map(id => {
          const num = missedCallNumbers.find(x => x.missedcall_numberid === id);
          return num ? num.virtual_number : id;
        }),
      };
    } else if (filterType === "Shortcode Response") {
      return {
        filtername: filterName,
        filterType: "shortcode",
        requiredData: shortcodeChips.join(","),
        dateFrom: sortcodeDateFrom,
        dateTill: sortcodeDateTill,
        shortcodes: selectedShortcodes,
        ...(selectedKeywords.length > 0 && { keywords: selectedKeywords }),
        ...(selectedSubKeywords.length > 0 && { subkeywords: selectedSubKeywords }),
      };
    }
    return null;
  };

  const handleGetCount = async () => {
    const payload = buildCountPayload();
    if (!payload) {
      toast.error("Count preview isn't supported for this filter type yet.");
      return;
    }

    setCountResult(null);
    setOpenSubmitDialog(true);

    try {
      const response = await submitpayload(payload).unwrap();
      if (response?.status) {
        setCountResult(response);
      } else {
        toast.error(response?.message || "Failed to get count");
      }
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to get count."));
    }
  };

  const handleCloseSubmitDialog = () => {
    setOpenSubmitDialog(false);
    setCountResult(null);
  };

  const handleSubmit = async () => {
    if (givePartialData && (!recordsPerPage || Number(recordsPerPage) <= 0)) {
      toast.error("Please enter a valid number of rows for partial data.");
      return false;
    }
    if (splitData && (!batchLimit || Number(batchLimit) <= 0)) {
      toast.error("Please enter a valid batch size for splitting data.");
      return false;
    }

    let payload = {};
    
    if (filterType === "DB") {
      const jsonData = selectedFields
        .map((field, i) => ({
          fieldName: field,
          searchType: operations[i],
          value: serializeFilterValue(operations[i], values[i]),
          logicalOperator: logicalOperators[i] || "AND",
        }))
        .filter((item) => item.fieldName && item.searchType && item.value !== "");
      
      payload = {
        filtername: filterName,
        requiredData: chips.join(","),
        jsonData,
        filterType: "db",
        givePartialData,
        ...(givePartialData && { recordsPerPage: Number(recordsPerPage) }),
        splitData,
        ...(splitData && { batchLimit: Number(batchLimit) }),
      };
      
      try {
        await submitFilters(payload).unwrap();
        toast.success("Filter submitted successfully!");
        resetDBStates();
        refetchSearchDataList();
        return true;
      } catch (err) {
        toast.error(getErrorMessage(err, "Failed to submit filter."));
        return false;
      }
    } else if (filterType === "Missedcall Response") {
      if (!missedCallDateFrom || !missedCallDateTill || selectedMissedCallNumbers.length === 0) {
        toast.error("Please fill all required fields for Missedcall Response");
        return false;
      }
      
      payload = {
        filtername: filterName,
        filterType: "missedcall",
        requiredData: missedCallChips.join(","),
        dateFrom: missedCallDateFrom,
        dateTill: missedCallDateTill,
        missedCallNumbers: selectedMissedCallNumbers.map(id => {
          const num = missedCallNumbers.find(x => x.missedcall_numberid === id);
          return num ? num.virtual_number : id;
        }),
        givePartialData,
        ...(givePartialData && { recordsPerPage: Number(recordsPerPage) }),
        splitData,
        ...(splitData && { batchLimit: Number(batchLimit) }),
      };
      
      try {
        const response = await saveFilterReport(payload).unwrap();
        if (response.success || response.status) {
          toast.success("Filter report saved successfully");
          resetAllStates();
          refetchSearchDataList();
          return true;
        }
        toast.error(response.message || response.error || "Failed to save filter report");
        return false;
      } catch (err) {
        toast.error(getErrorMessage(err, "Failed to submit filter."));
        return false;
      }
    } else if (filterType === "Shortcode Response") {
      if (!sortcodeDateFrom || !sortcodeDateTill || selectedShortcodes.length === 0) {
        toast.error("Please select date range and at least one shortcode");
        return false;
      }
      
      payload = {
        filtername: filterName,
        filterType: "shortcode",
        requiredData: shortcodeChips.join(","),
        dateFrom: sortcodeDateFrom,
        dateTill: sortcodeDateTill,
        shortcodes: selectedShortcodes,
        ...(selectedKeywords.length > 0 && { keywords: selectedKeywords }),
        ...(selectedSubKeywords.length > 0 && { subkeywords: selectedSubKeywords }),
        givePartialData,
        ...(givePartialData && { recordsPerPage: Number(recordsPerPage) }),
        splitData,
        ...(splitData && { batchLimit: Number(batchLimit) }),
      };
      
      try {
        const response = await saveFilterReport(payload).unwrap();
        if (response.success || response.status) {
          toast.success("Filter report saved successfully");
          resetAllStates();
          refetchSearchDataList();
          return true;
        }
        toast.error(response.message || response.error || "Failed to save filter report");
        return false;
      } catch (err) {
        toast.error(getErrorMessage(err, "Failed to submit filter."));
        return false;
      }
    }
  };

  const resetAllStates = () => {
    setFilterName(""); setChips([]);
    setSelectedFields([""]); setDataTypes([""]); setOperations([""]); setValues([""]);
    setLogicalOperators(["AND"]);
    setMissedCallDateFrom(""); setMissedCallDateTill(""); setSelectedMissedCallNumbers([]); setCallerNumbers([]);
    setSortcodeDateFrom(""); setSortcodeDateTill(""); setSelectedShortcodes([]);
    setSelectedKeywords([]); setSelectedSubKeywords([]); setMobileNumbers([]); setSelected("");
    setMissedCallChips([]); setShortcodeChips([]);
    setSelectedMissedCallField(""); setSelectedShortcodeField("");
    setGivePartialData(false); setSplitData(false); setRecordsPerPage(0); setBatchLimit(0);
  };

  const resetDBStates = () => {
    setFilterName(""); setChips([]);
    setSelectedFields([""]); setDataTypes([""]); setOperations([""]); setValues([""]);
    setLogicalOperators(["AND"]); setSelected("");
    setGivePartialData(false); setSplitData(false); setRecordsPerPage(0); setBatchLimit(0);
  };

  const isDateLike = (t = "") => /(date|time|timestamp|datetime)/i.test(t);
  const serializeFilterValue = (operation, value) =>
    ["before", "after"].includes(operation) && value !== "" ? Number(value) : value;

  const formatDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderFilterCriteria = (conditions = []) => {
    if (!conditions?.length) return "-";
    return conditions.map((c, i) => (
      <Typography key={`${c.fieldName}-${i}`} sx={{ fontSize: "0.78rem", lineHeight: 1.6 }}>
        {i > 0 && <><b>{String(c.logicalOperator || "AND").toUpperCase()}</b> </>}
        <b>{c.fieldName}</b> {c.searchType} <i>{c.value}</i>
      </Typography>
    ));
  };

  const handleDeleteClick = (customer_requestid) => {
    setDeleteTargetId(customer_requestid);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetId) return;
    try {
      await deleteSearchData(deleteTargetId).unwrap();
      toast.success("Filter deleted successfully!");
      refetchSearchDataList();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to delete filter."));
    } finally {
      setOpenDeleteDialog(false);
      setDeleteTargetId(null);
    }
  };

  // ── Update filter (Action → Edit) ────────────────────────────────────────
  const handleOpenEditDialog = (item) => {
    setEditTargetItem(item);
    setEditFilterName(item.filtername || "");
    const req = Array.isArray(item.fieldsinfile)
      ? item.fieldsinfile
      : (item.fieldsinfile ? String(item.fieldsinfile).split(",").filter(Boolean) : []);
    setEditChips(req);
    setEditSelected("");
    const conditions = item.customer_json_data?.length ? item.customer_json_data : [{}];
    setEditSelectedFields(conditions.map((c) => c.fieldName || ""));
    setEditDataTypes(conditions.map((c) => columns.find((col) => col.column_name === c.fieldName)?.data_type || ""));
    setEditOperations(conditions.map((c) => c.searchType || ""));
    setEditValues(conditions.map((c) => c.value || ""));
    setEditLogicalOperators(conditions.map((c) => String(c.logicalOperator || "AND").toUpperCase()));
    setOpenEditDialog(true);
  };

  const closeEditDialog = () => {
    setOpenEditDialog(false);
    setEditTargetItem(null);
  };

  const handleEditFieldSelect = (e) => {
    const v = e.target.value;
    if (!editChips.includes(v)) setEditChips((p) => [...p, v]);
    setEditSelected("");
  };

  const handleEditChipDelete = (chip) => setEditChips((p) => p.filter((x) => x !== chip));

  const onEditDragEnd = useCallback((result) => {
    if (!result.destination) return;
    const r = Array.from(editChips);
    const [m] = r.splice(result.source.index, 1);
    r.splice(result.destination.index, 0, m);
    setEditChips(r);
  }, [editChips]);

  const handleEditFieldChange = (index, fieldName) => {
    const f = [...editSelectedFields], d = [...editDataTypes], o = [...editOperations], v = [...editValues];
    const col = columns.find((c) => c.column_name === fieldName);
    f[index] = fieldName; d[index] = col?.data_type || ""; o[index] = ""; v[index] = "";
    setEditSelectedFields(f); setEditDataTypes(d); setEditOperations(o); setEditValues(v);
  };

  const handleEditOperationChange = (index, op) => {
    const o = [...editOperations], v = [...editValues];
    o[index] = op; v[index] = "";
    setEditOperations(o); setEditValues(v);
  };

  const handleEditValueChange = (index, val) => {
    const v = [...editValues]; v[index] = val; setEditValues(v);
  };

  const handleEditLogicalOperatorChange = (index, operator) => {
    const next = [...editLogicalOperators];
    next[index] = operator;
    setEditLogicalOperators(next);
  };

  const handleEditAddRow = () => {
    if (editSelectedFields.length < fieldList.length) {
      setEditSelectedFields([...editSelectedFields, ""]); setEditDataTypes([...editDataTypes, ""]);
      setEditOperations([...editOperations, ""]); setEditValues([...editValues, ""]);
      setEditLogicalOperators([...editLogicalOperators, "AND"]);
    }
  };

  const handleEditRemoveRow = (index) => {
    if (editSelectedFields.length > 1) {
      setEditSelectedFields(editSelectedFields.filter((_, i) => i !== index));
      setEditDataTypes(editDataTypes.filter((_, i) => i !== index));
      setEditOperations(editOperations.filter((_, i) => i !== index));
      setEditValues(editValues.filter((_, i) => i !== index));
      setEditLogicalOperators(editLogicalOperators.filter((_, i) => i !== index));
    }
  };

  const handleSaveEditFilter = async () => {
    if (!editTargetItem?.customer_requestid) {
      toast.error("No filter selected.");
      return;
    }
    if (!editFilterName.trim()) {
      toast.error("Please enter a filter name.");
      return;
    }
    if (editChips.length === 0) {
      toast.error("Please select at least one field.");
      return;
    }
    const jsonData = editSelectedFields
      .map((field, i) => ({
        fieldName: field,
        searchType: editOperations[i],
        value: serializeFilterValue(editOperations[i], editValues[i]),
        logicalOperator: editLogicalOperators[i] || "AND",
      }))
      .filter((item) => item.fieldName && item.searchType && item.value !== "");
    if (jsonData.length === 0) {
      toast.error("Please add at least one valid filter condition.");
      return;
    }

    // Matches the useUpdatesearchdatalistMutation API contract:
    // {
    //   "requestId": "18990",
    //   "filterName": "tc1 button1",
    //   "requiredData": "ph_no,address,account,pin_number,city,state",
    //   "jsonData": [{ "fieldName": "city", "searchType": "in", "value": "Delhi" }]
    // }
    const payload = {
      requestId: String(editTargetItem.customer_requestid),
      filterName: editFilterName.trim(),
      requiredData: editChips.join(","),
      jsonData,
    };

    try {
      const response = await updateSearchData(payload).unwrap();
      toast.success(response?.message || "Filter updated and new records appended successfully.");
      closeEditDialog();
      refetchSearchDataList();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to update filter."));
    }
  };

  // ── Cancel filter (Action → Cancel, only for pending/in-progress filters) ─
  const isCancellableStatus = (item) => {
    const raw = item.status || item.filter_status || item.filterStatus || item.request_status || "";
    const normalized = String(raw).toLowerCase().replace(/[\s_-]+/g, "");
    return normalized === "pending" || normalized === "inprogress";
  };

  const handleCancelClick = (item) => {
    setCancelTargetItem(item);
    setOpenCancelDialog(true);
  };

  const closeCancelDialog = () => {
    setOpenCancelDialog(false);
    setCancelTargetItem(null);
  };

  const handleConfirmCancel = async () => {
    if (!cancelTargetItem?.customer_requestid) return;
    try {
      // NOTE: assumes useCancelSearchDataMutation takes the request id
      // directly, mirroring useDeleteSearchDataMutation's shape above —
      // adjust if the backend expects an object payload instead.
      await cancelSearchData(cancelTargetItem.customer_requestid).unwrap();
      toast.success("Filter cancelled successfully!");
      refetchSearchDataList();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to cancel filter."));
    } finally {
      closeCancelDialog();
    }
  };

  const handleInfoClick = (item) => {
    setInfoDialogItem(item);
    setUsageHistoryId(item.customer_requestid);
    setInfoDialogOpen(true);
  };

  const handleCloseInfoDialog = () => {
    setInfoDialogOpen(false);
    setInfoDialogItem(null);
    setUsageHistoryId(null);
  };

  // ── Select Flow handlers (ported from EventCreation) ────────────────────
  const openFlowDialog = (item) => {
    setFlowDialogItem(item);
    setServiceType(item.flow_config?.serviceType || "");
    setSelectedFlow(item.flow_config?.flowName || "");
    setSelectedTemplate(item.flow_config?.template || "");
    setRunDuration(item.flow_config?.runDuration || "");
    setRunTime(item.flow_config?.runTime || "");
    setTimeZone(item.flow_config?.timeZone || "Asia/Kolkata");
    setNextRunAt(item.flow_config?.nextRunAt || "");
    setEventStatus(item.flow_config?.status || "paused");
    setSelectedDays(item.flow_config?.days || []);
    setCustomNoOfDays(item.flow_config?.customNoOfDays || "");
    setCustomScheduleBy(item.flow_config?.customScheduleBy || "day");
    setCustomDates(item.flow_config?.customDates || []);
    setCustomDateInput("");
    setCampaignType(item.flow_config?.campaignType || "");
    setFlowDialogOpen(true);
  };

  const closeFlowDialog = () => {
    setFlowDialogOpen(false);
    setFlowDialogItem(null);
  };

  const handleAddCustomDate = () => {
    if (!customDateInput) return;
    if (customDates.includes(customDateInput)) {
      setCustomDateInput("");
      return;
    }
    setCustomDates((p) => [...p, customDateInput].sort());
    setCustomDateInput("");
  };

  const handleRemoveCustomDate = (d) => setCustomDates((p) => p.filter((x) => x !== d));

  const handleServiceChange = (e) => {
    setServiceType(e.target.value);
    setSelectedFlow("");
    setSelectedTemplate("");
  };

  // Resolve the human-readable template name for whichever template id is
  // currently selected, since the API wants "template_name", not an id.
  const getSelectedTemplateName = () => {
    if (serviceType === "wa") {
      return waTemplates.find((t) => t.template_id === selectedTemplate)?.name;
    }
    if (serviceType === "rcs") {
      return rcsTemplates.find((t) => t.id === selectedTemplate)?.name;
    }
    if (serviceType === "sms") {
      return smsTemplates.find((t) => t.template_id === selectedTemplate)?.template_name;
    }
    return undefined;
  };

  const handleSaveFlow = async () => {
    if (!flowDialogItem?.customer_requestid) {
      toast.error("No filter selected.");
      return;
    }
    if (!serviceType) {
      toast.error("Please select a service (Flow / SMS / RCS / WA).");
      return;
    }
    if (serviceType === "flow" && !selectedFlow) {
      toast.error("Please select a flow.");
      return;
    }
    if (serviceType !== "flow" && !selectedTemplate) {
      toast.error("Please select a template.");
      return;
    }
    if (!runDuration || !runTime || !timeZone || !nextRunAt) {
      toast.error("Please fill run duration, run time, time zone and next run at.");
      return;
    }
    if (runDuration === "custom") {
      const days = Number(customNoOfDays);
      if (!customNoOfDays || days <= 0 || days > MAX_CUSTOM_DAYS) {
        toast.error(`Please enter a valid number of days (1-${MAX_CUSTOM_DAYS}).`);
        return;
      }
      if (customScheduleBy === "date" && customDates.length === 0) {
        toast.error("Please add at least one date for a date-wise custom schedule.");
        return;
      }
    }
    if (!campaignType) {
      toast.error("Please select a campaign type.");
      return;
    }

    // Matches the createEventFlow API contract used in Event Creation:
    // {
    //   "event_id": 1,
    //   "flow_id": 4,
    //   "service_type": "Flow",
    //   "template_name": "Testing",
    //   "schedule_type": "DAILY",
    //   "schedule_config": { "days": ["MONDAY", "TUESDAY", "WEDNESDAY"] },
    //   "run_time": "10:30:00",
    //   "timezone": "Asia/Kolkata",
    //   "next_run_at": "2026-08-13T10:30:00+05:30"
    // }
    // NOTE: "event_id" is populated with this filter's customer_requestid —
    // update the key name here if the backend expects something different
    // for DB-filter-driven flows.
    const scheduleConfig =
      runDuration === "custom"
        ? {
            no_of_days: Number(customNoOfDays),
            schedule_by: customScheduleBy,
            ...(customScheduleBy === "day" ? { days: selectedDays } : {}),
            ...(customScheduleBy === "date" ? { dates: customDates } : {}),
          }
        : { days: selectedDays };

    const payload = {
      event_id: flowDialogItem.customer_requestid,
      ...(serviceType === "flow" ? { flow_id: selectedFlow } : {}),
      service_type: SERVICE_TYPE_LABELS[serviceType],
      ...(serviceType !== "flow" ? { template_name: getSelectedTemplateName() } : {}),
      schedule_type: runDuration.toUpperCase(),
      schedule_config: scheduleConfig,
      run_time: runTime.length === 5 ? `${runTime}:00` : runTime,
      timezone: timeZone,
      next_run_at: buildNextRunAtISO(nextRunAt, timeZone),
      campaign_type: campaignType,
    };

    try {
      await createEventFlow(payload).unwrap();
      toast.success("Flow saved successfully!");
      closeFlowDialog();
      refetchSearchDataList();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to save flow."));
    }
  };

  // ── WhatsApp Template Preview Helper (ported from EventCreation) ───────
  const renderWhatsAppPreview = (tpl) => {
    if (!tpl) return <Typography>No template data available</Typography>;

    // Extract components
    const bodyComponent = tpl.components?.find((c) => c.type === "BODY");
    const headerComponent = tpl.components?.find((c) => c.type === "HEADER");
    const footerComponent = tpl.components?.find((c) => c.type === "FOOTER");
    const buttonsComponent = tpl.components?.find((c) => c.type === "BUTTONS");
    const carouselComponent = tpl.components?.find((c) => c.type === "CAROUSEL");

    // Get body text
    const bodyText = bodyComponent?.text || "";

    // Get header media
    let mediaUrl = null;
    let mediaFormat = null;

    if (headerComponent?.format === "IMAGE" || headerComponent?.format === "VIDEO") {
      const headerHandle = headerComponent?.example?.header_handle;
      if (Array.isArray(headerHandle) && headerHandle.length > 0) {
        mediaUrl = headerHandle[0];
        mediaFormat = headerComponent.format;
      }
    }

    // Handle carousel templates
    if (carouselComponent?.cards && carouselComponent.cards.length > 0) {
      return (
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", mb: 2 }}>{tpl.name}</Typography>

          {/* Template Info */}
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Chip size="small" label={`Category: ${tpl.category}`} sx={{ fontSize: "0.72rem", background: "#EEF6FC" }} />
            <Chip size="small" label={`Type: ${tpl.template_type}`} sx={{ fontSize: "0.72rem", background: "#EEF6FC" }} />
          </Stack>

          {/* Display carousel cards */}
          <TemplateCardCarousel cards={carouselComponent.cards} />

          {/* Main body text below carousel */}
          {bodyText && (
            <Typography sx={{ mt: 2 }}>{bodyText}</Typography>
          )}
        </Box>
      );
    }

    // Regular template display
    return (
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: "1rem", mb: 1.5 }}>{tpl.name}</Typography>

        {/* Media header */}
        {mediaUrl && (
          <Box sx={{ mb: 2, borderRadius: 2, overflow: "hidden" }}>
            {mediaFormat === "VIDEO" ? (
              <video
                src={mediaUrl}
                controls
                style={{ width: "100%", maxHeight: 220, objectFit: "cover" }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentNode.innerHTML = '<div style="padding: 20px; text-align: center; background: #f0f0f0; border-radius: 8px;">Video not available</div>';
                }}
              />
            ) : (
              <img
                src={mediaUrl}
                alt="preview"
                style={{ width: "100%", maxHeight: 220, objectFit: "cover" }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentNode.innerHTML = '<div style="padding: 20px; text-align: center; background: #f0f0f0; border-radius: 8px;">Image not available</div>';
                }}
              />
            )}
          </Box>
        )}

        {/* Template info */}
        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
          <Chip size="small" label={`Category: ${tpl.category}`} sx={{ fontSize: "0.72rem", background: "#EEF6FC" }} />
          <Chip size="small" label={`Type: ${tpl.template_type}`} sx={{ fontSize: "0.72rem", background: "#EEF6FC" }} />
          <Chip size="small" label={`Language: ${tpl.language}`} sx={{ fontSize: "0.72rem", background: "#EEF6FC" }} />
        </Stack>

        {/* Body text */}
        {bodyText && (
          <Box sx={{ p: 1.5, borderRadius: 2, background: "#F7F9FB", border: "1px solid #E6ECF1" }}>
            <Typography sx={{ whiteSpace: "pre-wrap", fontSize: "0.9rem", lineHeight: 1.6 }}>
              {bodyText}
            </Typography>
          </Box>
        )}

        {/* Footer */}
        {footerComponent?.text && (
          <Typography sx={{ mt: 1.5, fontSize: "0.82rem", color: "text.secondary", fontStyle: "italic" }}>
            {footerComponent.text}
          </Typography>
        )}

        {/* Buttons */}
        {buttonsComponent?.buttons && buttonsComponent.buttons.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={sectionLabelSx}>Buttons</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {buttonsComponent.buttons.map((btn, index) => (
                <Chip
                  key={index}
                  label={`${btn.text || btn.type}${btn.url ? ` (${btn.url})` : ''}`}
                  size="small"
                  sx={{ borderRadius: "8px" }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  // ── Render DB Filter ───────────────────────────────────────────────────────
  const renderDBFilter = () => (
    <>
      {/* Chip drag-drop area */}
      <Box
        sx={{
          border: "1px solid #E2E8F0",
          borderRadius: 0.5,
          p: 2,
          display: "flex",
          alignItems: "flex-start",
          gap: 3,
          mt: 2,
          background: "rgba(255,255,255,0.5)",
          backdropFilter: "blur(8px)",
        }}
      >
        <Box sx={{ flex: 2, display: "flex", flexDirection: "row", gap: 4 }}>
          <Box>
            <Typography variant="h6" sx={{ mb: 1.8, fontSize: "0.9rem", fontWeight: 600 }}>
              Add Fields
            </Typography>
            <FormControl size="small">
              <Select value={selected} onChange={handleSelect} displayEmpty sx={smallSelectSx}>
                <MenuItem value="" disabled sx={{ fontSize: "0.8rem" }}>Select option</MenuItem>
                {fieldList.map((name) => (
                  <MenuItem key={name} value={name} disabled={chips.includes(name)} sx={{ fontSize: "0.8rem" }}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ mb: 1, fontSize: "0.9rem", fontWeight: 600 }}>
              Selected Fields Sequence
            </Typography>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="chips" direction="horizontal">
                {(provided) => (
                  <Stack direction="row" spacing={1} sx={chipAreaSx} ref={provided.innerRef} {...provided.droppableProps}>
                    {chips.map((chip, index) => (
                      <Draggable key={chip} draggableId={chip} index={index} isDragDisabled={chip === "ph_no" && index === 0}>
                        {(provided) => (
                          <Chip
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...(chip !== "ph_no" || index !== 0 ? provided.dragHandleProps : {})}
                            label={chip}
                            onDelete={() => chip !== "ph_no" || index !== 0 ? handleDelete(chip) : null}
                            variant="outlined"
                            sx={{ borderRadius: "10px", background: chip === "ph_no" && index === 0 ? "rgba(33, 150, 243, 0.15)" : "rgba(255,255,255,0.85)", backdropFilter: "blur(4px)", fontSize: "0.82rem", fontWeight: 500, opacity: chip === "ph_no" && index === 0 ? 0.8 : 1 }}
                          />
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Stack>
                )}
              </Droppable>
            </DragDropContext>
          </Box>
        </Box>
      </Box>

      {/* ── Filters Table — styled like WorkFlow ── */}
      <Paper elevation={1} sx={{ borderRadius: 1, overflow: "hidden", mt: 2 }}>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table stickyHeader size="small" sx={{ minWidth: 900 }}>
            <TableHead sx={{ bgcolor: "#0288d1" }}>
              <TableRow>
                <TableCell sx={thCell}>Join</TableCell>
                <TableCell sx={thCell}>Fields</TableCell>
                <TableCell sx={thCell}>Operation</TableCell>
                <TableCell sx={{ ...thCell, borderRight: "none" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    Value
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleAddRow}
                      disabled={selectedFields.length >= fieldList.length}
                      endIcon={<AddIcon />}
                      sx={{
                        fontSize: "0.72rem",
                        textTransform: "none",
                        color: "white",
                        borderColor: "rgba(255,255,255,0.6)",
                        "&:hover": { borderColor: "white", background: "rgba(255,255,255,0.1)" },
                      }}
                    >
                      Add Filter
                    </Button>
                  </Box>
                </TableCell>
                <TableCell sx={{ ...thCell, borderRight: "none" }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ background: "rgba(255,255,255,0.23)" }}>
              {selectedFields.map((field, index) => (
                <TableRow
                  key={index}
                  hover
                  sx={{
                    "& td": { fontSize: "0.82rem", py: 1 },
                    "&:hover": { background: "rgba(186,230,253,0.2)" },
                    transition: "background 0.2s",
                  }}
                >
                  <TableCell sx={tdCell}>
                    {index === 0 ? (
                      <Typography sx={{ fontSize: "0.72rem", color: "text.secondary", textAlign: "center" }}>-</Typography>
                    ) : (
                      <FormControl fullWidth size="small" sx={{ minWidth: 90 }}>
                        <Select
                          value={logicalOperators[index] || "AND"}
                          onChange={(e) => handleLogicalOperatorChange(index, e.target.value)}
                          sx={smallSelectSx}
                          inputProps={{ "aria-label": `Logical operator before filter ${index + 1}` }}
                        >
                          <MenuItem value="AND" sx={{ fontSize: "0.8rem" }}>AND</MenuItem>
                          <MenuItem value="OR" sx={{ fontSize: "0.8rem" }}>OR</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  </TableCell>
                  <TableCell sx={tdCell}>
                    <FormControl fullWidth size="small" sx={{ minWidth: 150 }}>
                      <InputLabel id={`field-label-${index}`} sx={{ fontSize: "0.8rem" }}>Field</InputLabel>
                      <Select
                        labelId={`field-label-${index}`}
                        value={field}
                        label="Field"
                        onChange={(e) => handleFieldChange(index, e.target.value)}
                        sx={smallSelectSx}
                      >
                        {columns.map((col) => (
                          <MenuItem
                            key={col.column_name}
                            value={col.column_name}
                            disabled={selectedFields.some((f, i) => f === col.column_name && i !== index)}
                            sx={{ fontSize: "0.8rem" }}
                          >
                            {col.column_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={tdCell}>
                    <FormControl fullWidth size="small">
                      <InputLabel id={`op-label-${index}`} sx={{ fontSize: "0.8rem" }}>Operation</InputLabel>
                      <Select
                        labelId={`op-label-${index}`}
                        value={operations[index] || ""}
                        label="Operation"
                        onChange={(e) => handleOperationChange(index, e.target.value)}
                        sx={smallSelectSx}
                      >
                        {[
                          { value: "equal", label: "Equal" },
                          { value: "in", label: "In" },
                          { value: "contain", label: "Contain" },
                          { value: "greater_than", label: "Greater than" },
                          { value: "less_than", label: "Less than" },
                          { value: "between", label: "Between" },
                          { value: "start with", label: "Start with" },
                          { value: "end with", label: "End with" },
                          { value: "is", label: "Is" },
                          { value: "before", label: "Days before" },
                          { value: "after", label: "Days after" },
                        ].map(({ value, label }) => (
                          <MenuItem key={value} value={value} sx={{ fontSize: "0.8rem" }}>
                            {label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={{ ...tdCell, borderRight: "0.5px solid #d0d0d0" }}>
                    {["before", "after"].includes(operations[index]) ? (
                      <FormControl fullWidth size="small">
                        <Select
                          value={values[index] || ""}
                          displayEmpty
                          onChange={(e) => handleValueChange(index, e.target.value)}
                          sx={smallSelectSx}
                          inputProps={{ "aria-label": `Days ${operations[index]} date` }}
                        >
                          <MenuItem value="" disabled>Select days</MenuItem>
                          {Array.from({ length: 30 }, (_, dayIndex) => dayIndex + 1).map((day) => (
                            <MenuItem key={day} value={day}>{day}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : operations[index] === "between" ? (
                      <Box sx={{ display: "flex", gap: 1 }}>
                        {isDateLike(dataTypes[index]) ? (
                          <>
                            <TextField size="small" fullWidth type="datetime-local" InputLabelProps={{ shrink: true }}
                              value={values[index]?.split("|")[0] || ""}
                              onChange={(e) => handleValueChange(index, `${e.target.value}|${values[index]?.split("|")[1] || ""}`)}
                              sx={{ "& .MuiOutlinedInput-root": glassInput }}
                            />
                            <TextField size="small" fullWidth type="datetime-local" InputLabelProps={{ shrink: true }}
                              value={values[index]?.split("|")[1] || ""}
                              onChange={(e) => handleValueChange(index, `${values[index]?.split("|")[0] || ""}|${e.target.value}`)}
                              sx={{ "& .MuiOutlinedInput-root": glassInput }}
                            />
                          </>
                        ) : (
                          <>
                            <TextField size="small" placeholder="From"
                              value={values[index]?.split("|")[0] || ""}
                              onChange={(e) => handleValueChange(index, `${e.target.value}|${values[index]?.split("|")[1] || ""}`)}
                              sx={{ "& .MuiOutlinedInput-root": glassInput }}
                            />
                            <TextField size="small" placeholder="To"
                              value={values[index]?.split("|")[1] || ""}
                              onChange={(e) => handleValueChange(index, `${values[index]?.split("|")[0] || ""}|${e.target.value}`)}
                              sx={{ "& .MuiOutlinedInput-root": glassInput }}
                            />
                          </>
                        )}
                      </Box>
                    ) : isDateLike(dataTypes[index]) ? (
                      <TextField size="small" fullWidth type="datetime-local" InputLabelProps={{ shrink: true }}
                        value={values[index] || ""}
                        onChange={(e) => handleValueChange(index, e.target.value)}
                        sx={{ "& .MuiOutlinedInput-root": glassInput }}
                      />
                    ) : (
                      <>
                        <TextField size="small" fullWidth placeholder="Enter value"
                          value={values[index] || ""}
                          onChange={(e) => handleValueInputChange(index, e.target.value)}
                          sx={{ "& .MuiOutlinedInput-root": glassInput }}
                        />
                        {suggestionsMap[index] && suggestionsMap[index].length > 0 && (
                          <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {suggestionsMap[index].map((s) => (
                              <Chip
                                key={s.value}
                                label={s.label || s.value}
                                size="small"
                                onClick={() => handleSuggestionSelect(index, s.value)}
                                sx={{ cursor: 'pointer' }}
                              />
                            ))}
                          </Box>
                        )}
                      </>
                    )}
                  </TableCell>
                  <TableCell sx={{ ...tdCell, borderRight: "none" }} align="center">
                    {selectedFields.length > 1 && (
                      <Button color="error" size="small" onClick={() => handleRemoveRow(index)}
                        sx={{ fontSize: "0.75rem", textTransform: "none" }}>
                        Remove
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );

  // ── Render Missed Call Filter ──────────────────────────────────────────────
  const renderMissedCallFilter = () => (
    <>
      {/* Chip drag-drop area for Missed Call Fields Sequence */}
      <Box
        sx={{
          border: "1px solid #E2E8F0",
          borderRadius: 0.5,
          p: 2,
          display: "flex",
          alignItems: "flex-start",
          gap: 3,
          mt: 2,
          background: "rgba(255,255,255,0.5)",
          backdropFilter: "blur(8px)",
        }}
      >
        <Box sx={{ flex: 2, display: "flex", flexDirection: "row", gap: 4 }}>
          <Box>
            <Typography variant="h6" sx={{ mb: 1.8, fontSize: "0.9rem", fontWeight: 600 }}>
              Add Missed Call Fields
            </Typography>
            <FormControl size="small">
              <Select 
                value={selectedMissedCallField} 
                onChange={handleMissedCallFieldSelect} 
                displayEmpty 
                sx={smallSelectSx}
              >
                <MenuItem value="" disabled sx={{ fontSize: "0.8rem" }}>Select option</MenuItem>
                {missedCallFields.map((item) => (
                  <MenuItem 
                    key={item.value} 
                    value={item.value} 
                    disabled={missedCallChips.includes(item.value)} 
                    sx={{ fontSize: "0.8rem" }}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ mb: 1, fontSize: "0.9rem", fontWeight: 600 }}>
              Selected Fields Sequence
            </Typography>
            <DragDropContext onDragEnd={onMissedCallDragEnd}>
              <Droppable droppableId="missedCallChips" direction="horizontal">
                {(provided) => (
                  <Stack 
                    direction="row" 
                    spacing={1} 
                    sx={chipAreaSx} 
                    ref={provided.innerRef} 
                    {...provided.droppableProps}
                  >
                    {missedCallChips.map((chip, index) => (
                      <Draggable key={chip} draggableId={chip} index={index}>
                        {(provided) => (
                          <Chip
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            label={getMissedCallFieldLabel(chip)}
                            onDelete={() => handleMissedCallChipDelete(chip)}
                            variant="outlined"
                            sx={{ 
                              borderRadius: "10px", 
                              background: "rgba(255,255,255,0.85)", 
                              backdropFilter: "blur(4px)", 
                              fontSize: "0.82rem", 
                              fontWeight: 500 
                            }}
                          />
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Stack>
                )}
              </Droppable>
            </DragDropContext>
          </Box>
        </Box>
      </Box>

      {/* Filters Table with blue header - Below Sequence Selector */}
      <Paper elevation={1} sx={{ borderRadius: 1, overflow: "hidden", mt: 2 }}>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table stickyHeader size="small" sx={{ minWidth: 900 }}>
            <TableHead sx={{ bgcolor: "#0288d1" }}>
              <TableRow>
                <TableCell sx={thCell}>Date Range</TableCell>
                <TableCell sx={thCell}>Missedcall Numbers</TableCell>
                <TableCell sx={{ ...thCell, borderRight: "none" }}>Caller Numbers (Auto-fetched)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ background: "rgba(255,255,255,0.23)" }}>
              <TableRow hover>
                <TableCell sx={tdCell}>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <TextField label="Start Date" type="date" value={missedCallDateFrom}
                      onChange={(e) => setMissedCallDateFrom(e.target.value)}
                      size="small" InputLabelProps={{ shrink: true }} InputProps={{ inputProps: { max: today } }}
                      sx={dateFieldSx}
                    />
                    <TextField label="End Date" type="date" value={missedCallDateTill}
                      onChange={(e) => setMissedCallDateTill(e.target.value)}
                      size="small" InputLabelProps={{ shrink: true }} InputProps={{ inputProps: { max: today } }}
                      sx={dateFieldSx}
                    />
                  </Box>
                </TableCell>
                <TableCell sx={tdCell}>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel sx={{ fontSize: "0.75rem" }}>Missedcall Numbers</InputLabel>
                    <Select
                      multiple value={selectedMissedCallNumbers} onChange={handleMissedCallNumberChange}
                      renderValue={(sel) => sel.map((id) => { const n = missedCallNumbers.find((x) => x.missedcall_numberid === id); return n ? n.virtual_number : id; }).join(", ")}
                      label="Missedcall Numbers" sx={smallSelectSx}
                    >
                      {missedCallNumbers.map((num) => (
                        <MenuItem key={num.missedcall_numberid} value={num.missedcall_numberid} sx={{ fontSize: "0.75rem" }}>
                          <Checkbox checked={selectedMissedCallNumbers.indexOf(num.missedcall_numberid) > -1} size="small" />
                          <ListItemText primary={num.virtual_number} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell sx={{ ...tdCell, borderRight: "none" }}>
                  {callerNumbers.length > 0 ? (
                    <Box sx={{ p: 1, border: "1px solid #E2E8F0", borderRadius: 2, background: "rgba(255,255,255,0.6)", backdropFilter: "blur(6px)", maxHeight: 80, overflowY: "auto" }}>
                      <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>{callerNumbers.join(", ")}</Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: "0.75rem" }}>
                      Select numbers and dates to fetch caller numbers
                    </Typography>
                  )}
                  {missedCallReportData?.data?.length > 0 && selectedMissedCallNumbers.length > 0 && missedCallDateFrom && missedCallDateTill && (
                    <Typography variant="body2" color="success.main" sx={{ fontSize: "0.75rem", mt: 1 }}>
                      Data found for selected criteria
                    </Typography>
                  )}
                  {missedCallReportData?.data?.length === 0 && selectedMissedCallNumbers.length > 0 && missedCallDateFrom && missedCallDateTill && (
                    <Typography variant="body2" color="error" sx={{ fontSize: "0.75rem", mt: 1 }}>
                      No data found for selected criteria
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );

  // ── Render Shortcode Filter ────────────────────────────────────────────────
  const renderShortcodeFilter = () => (
    <>
      {/* Chip drag-drop area for Shortcode Fields Sequence */}
      <Box
        sx={{
          border: "1px solid #E2E8F0",
          borderRadius: 0.5,
          p: 2,
          display: "flex",
          alignItems: "flex-start",
          gap: 3,
          mt: 2,
          background: "rgba(255,255,255,0.5)",
          backdropFilter: "blur(8px)",
        }}
      >
        <Box sx={{ flex: 2, display: "flex", flexDirection: "row", gap: 4 }}>
          <Box>
            <Typography variant="h6" sx={{ mb: 1.8, fontSize: "0.9rem", fontWeight: 600 }}>
              Add Shortcode Fields
            </Typography>
            <FormControl size="small">
              <Select 
                value={selectedShortcodeField} 
                onChange={handleShortcodeFieldSelect} 
                displayEmpty 
                sx={smallSelectSx}
              >
                <MenuItem value="" disabled sx={{ fontSize: "0.8rem" }}>Select option</MenuItem>
                {shortcodeFields.map((item) => (
                  <MenuItem 
                    key={item.value} 
                    value={item.value} 
                    disabled={shortcodeChips.includes(item.value)} 
                    sx={{ fontSize: "0.8rem" }}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ mb: 1, fontSize: "0.9rem", fontWeight: 600 }}>
              Selected Fields Sequence
            </Typography>
            <DragDropContext onDragEnd={onShortcodeDragEnd}>
              <Droppable droppableId="shortcodeChips" direction="horizontal">
                {(provided) => (
                  <Stack 
                    direction="row" 
                    spacing={1} 
                    sx={chipAreaSx} 
                    ref={provided.innerRef} 
                    {...provided.droppableProps}
                  >
                    {shortcodeChips.map((chip, index) => (
                      <Draggable key={chip} draggableId={chip} index={index}>
                        {(provided) => (
                          <Chip
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            label={getShortcodeFieldLabel(chip)}
                            onDelete={() => handleShortcodeChipDelete(chip)}
                            variant="outlined"
                            sx={{ 
                              borderRadius: "10px", 
                              background: "rgba(255,255,255,0.85)", 
                              backdropFilter: "blur(4px)", 
                              fontSize: "0.82rem", 
                              fontWeight: 500 
                            }}
                          />
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Stack>
                )}
              </Droppable>
            </DragDropContext>
          </Box>
        </Box>
      </Box>

      {/* Filters Table with blue header - Below Sequence Selector */}
      <Paper elevation={1} sx={{ borderRadius: 1, overflow: "hidden", mt: 2 }}>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table stickyHeader size="small" sx={{ minWidth: 900 }}>
            <TableHead sx={{ bgcolor: "#0288d1" }}>
              <TableRow>
                <TableCell sx={thCell}>Date Range</TableCell>
                <TableCell sx={thCell}>Shortcodes</TableCell>
                <TableCell sx={thCell}>Keywords</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ background: "rgba(255,255,255,0.23)" }}>
              <TableRow hover>
                <TableCell sx={tdCell}>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <TextField label="Start Date" type="date" value={sortcodeDateFrom}
                      onChange={(e) => setSortcodeDateFrom(e.target.value)}
                      size="small" InputLabelProps={{ shrink: true }} InputProps={{ inputProps: { max: today } }}
                      sx={dateFieldSx}
                    />
                    <TextField label="End Date" type="date" value={sortcodeDateTill}
                      onChange={(e) => setSortcodeDateTill(e.target.value)}
                      size="small" InputLabelProps={{ shrink: true }} InputProps={{ inputProps: { max: today } }}
                      sx={dateFieldSx}
                    />
                  </Box>
                </TableCell>
                <TableCell sx={tdCell}>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel sx={{ fontSize: "0.75rem" }}>Shortcodes</InputLabel>
                    <Select multiple value={selectedShortcodes} onChange={handleShortcodeChange} 
                      renderValue={(sel) => sel.join(", ")} label="Shortcodes" sx={smallSelectSx}>
                      {shortcodes.map((item) => (
                        <MenuItem key={item.shortcode} value={item.shortcode} sx={{ fontSize: "0.75rem" }}>
                          <Checkbox checked={selectedShortcodes.indexOf(item.shortcode) > -1} size="small" />
                          <ListItemText primary={item.shortcode} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell sx={{ ...tdCell, borderRight: "none" }}>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel sx={{ fontSize: "0.75rem" }}>Keywords</InputLabel>
                    <Select multiple value={selectedKeywords} onChange={handleKeywordChange} 
                      renderValue={(sel) => sel.join(", ")} label="Keywords" 
                      disabled={availableKeywords.length === 0} sx={smallSelectSx}>
                      {availableKeywords.map((item) => (
                        <MenuItem key={item} value={item} sx={{ fontSize: "0.75rem" }}>
                          <Checkbox checked={selectedKeywords.indexOf(item) > -1} size="small" />
                          <ListItemText primary={item} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
              </TableRow>
              {/* Mobile Numbers Row */}
              {mobileNumbers.length > 0 && (
                <TableRow hover>
                  <TableCell colSpan={3} sx={{ ...tdCell, borderRight: "none" }}>
                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: "0.75rem", mb: 0.5 }}>Mobile Numbers:</Typography>
                    <Box sx={{ p: 1, border: "1px solid #E2E8F0", borderRadius: 2, background: "rgba(255,255,255,0.6)", backdropFilter: "blur(6px)", maxHeight: 80, overflowY: "auto" }}>
                      <Typography variant="body2" color="textSecondary" sx={{ fontSize: "0.75rem" }}>{mobileNumbers.join(", ")}</Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary">Total: {mobileNumbers.length} unique mobile numbers</Typography>
                  </TableCell>
                </TableRow>
              )}
              {shortcodeReportData?.data?.length > 0 && selectedShortcodes.length > 0 && sortcodeDateFrom && sortcodeDateTill && (
                <TableRow>
                  <TableCell colSpan={3} sx={{ ...tdCell, borderRight: "none" }}>
                    <Typography variant="body2" color="success.main" sx={{ fontSize: "0.75rem" }}>
                      Data found for selected criteria
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {shortcodeReportData?.data?.length === 0 && selectedShortcodes.length > 0 && sortcodeDateFrom && sortcodeDateTill && (
                <TableRow>
                  <TableCell colSpan={3} sx={{ ...tdCell, borderRight: "none" }}>
                    <Typography variant="body2" color="error" sx={{ fontSize: "0.75rem" }}>No data found for selected criteria</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );

  // ── Render Submitted Filters Table ─────────────────────────────────────────
  const renderSubmittedFiltersTable = () => (
    <Paper elevation={1} sx={{ borderRadius: 1, overflow: "hidden", mt: 3 }}>
      <Typography sx={{ p: 1.5, fontSize: "0.9rem", fontWeight: 600 }}>
        Submitted Filters
      </Typography>
      <TableContainer sx={{ maxHeight: 480 }}>
        <Table stickyHeader size="small" sx={{ minWidth: 900 }}>
          <TableHead sx={{ bgcolor: "#0288d1" }}>
            <TableRow>
              <TableCell sx={thCell}>Filter Name</TableCell>
              <TableCell sx={thCell}>Fields In File</TableCell>
              <TableCell sx={thCell}>Filter Criteria</TableCell>
              <TableCell sx={thCell}>Total Records</TableCell>
              <TableCell sx={thCell}>Updated/Created date</TableCell>
              <TableCell sx={thCell}>Status</TableCell>
              <TableCell sx={thCell}>Usage History</TableCell>
              <TableCell sx={{ ...thCell, borderRight: "none" }} align="center">Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody sx={{ background: "rgba(255,255,255,0.23)" }}>
            {isSearchDataListLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={tdCell}>
                  Loading...
                </TableCell>
              </TableRow>
            ) : searchDataList?.data?.length > 0 ? (
              searchDataList.data.map((item) => (
                <TableRow
                  key={item.customer_requestid}
                  hover
                  sx={{
                    "& td": { fontSize: "0.82rem", py: 1 },
                    "&:hover": { background: "rgba(186,230,253,0.2)" },
                  }}
                >
                  <TableCell sx={tdCell}>{item.filtername}</TableCell>

                  <TableCell sx={tdCell}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {item.fieldsinfile?.map((f) => (
                        <Chip key={f} label={f} size="small" variant="outlined" sx={{ fontSize: "0.7rem" }} />
                      ))}
                    </Box>
                  </TableCell>

                  <TableCell sx={tdCell}>{renderFilterCriteria(item.customer_json_data)}</TableCell>

                  <TableCell sx={tdCell} align="center">
                    {item.total_count}
                  </TableCell>

                  <TableCell sx={tdCell}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      {formatDate(item.updatedat)}
                    </Box>
                  </TableCell>

                  <TableCell sx={tdCell}>
                    {item.status || item.filter_status || item.filterStatus || item.request_status || "-"}
                  </TableCell>

                  <TableCell sx={tdCell}>
                    <Tooltip title="View SMS & Clicker History">
                      <IconButton
                        size="small"
                        onClick={() => handleInfoClick(item)}
                        sx={{ p: 0.3, color: "#0288d1" }}
                      >
                        <InfoOutlinedIcon sx={{ fontSize: "1rem" }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>

                  <TableCell sx={{ ...tdCell, borderRight: "none" }} align="center">
                    <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                      <Tooltip title="Edit filter">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEditDialog(item)}
                          sx={{ p: 0.4, color: "#0288d1" }}
                        >
                          <EditIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>
                      {isCancellableStatus(item) && (
                        <Tooltip title="Cancel filter">
                          <IconButton
                            size="small"
                            onClick={() => handleCancelClick(item)}
                            sx={{ p: 0.4, color: "#ed6c02" }}
                          >
                            <CancelRoundedIcon sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Button
                        color="error"
                        size="small"
                        onClick={() => handleDeleteClick(item.customer_requestid)}
                        sx={{ fontSize: "0.75rem", textTransform: "none" }}
                      >
                        Delete
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={tdCell}>
                  No filters submitted yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, py: 1.5 }}>
        <Typography sx={{ fontSize: "0.78rem" }}>
          {searchDataList?.totalRecords
            ? `Showing page ${searchDataList.currentPage} of ${searchDataList.totalPages} (${searchDataList.totalRecords} total)`
            : "No records"}
        </Typography>
        <Pagination
          count={searchDataList?.totalPages || 1}
          page={dataListPage}
          onChange={(e, value) => setDataListPage(value)}
          size="small"
          color="primary"
        />
      </Box>
    </Paper>
  );

  // ── Main Return ────────────────────────────────────────────────────────────
  return (
    <Box sx={layoutCSS}>
      <Box sx={{ width: "100%", overflowX: "auto", mb: 2, mt: 2 }}>

        {/* Filter Name */}
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontSize: "0.8rem", fontWeight: 600 }}>Filter Name</Typography>
          <TextField size="small" value={filterName} onChange={(e) => setFilterName(e.target.value)} sx={filterNameFieldSx} />
          <Typography fontSize="0.65rem" color="textSecondary" sx={{ mt: 0.3 }}>Write the name of the filter</Typography>
        </Box>

        {/* Filter Type */}
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontSize: "0.8rem", fontWeight: 600 }}>Filter Type</Typography>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              sx={{ ...smallSelectSx, height: 32 }}
            >
              <MenuItem value="DB" sx={{ fontSize: "0.75rem" }}>DB</MenuItem>
              <MenuItem value="Missedcall Response" sx={{ fontSize: "0.75rem" }}>Missedcall Response</MenuItem>
              <MenuItem value="Shortcode Response" sx={{ fontSize: "0.75rem" }}>Shortcode Response</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {filterType === "DB" && renderDBFilter()}
        {filterType === "Missedcall Response" && renderMissedCallFilter()}
        {filterType === "Shortcode Response" && renderShortcodeFilter()}
      </Box>

      <Button
        variant="contained"
        color="primary"
        sx={{ mt: 2, textTransform: "none", borderRadius: "8px" }}
        onClick={handleGetCount}
        disabled={isCountLoading}
      >
        {isCountLoading ? "Getting Count..." : "Get Count"}
      </Button>

      {renderSubmittedFiltersTable()}

      {/* Submit Dialog */}
      <Dialog open={openSubmitDialog} onClose={handleCloseSubmitDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: "0.9rem", fontWeight: 600 }}>Submit Filter</DialogTitle>
        <DialogContent dividers>
          {isCountLoading ? (
            <Typography sx={{ fontSize: "0.85rem", mb: 2 }}>Fetching count...</Typography>
          ) : countResult ? (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 1,
                background: "rgba(2,136,209,0.08)",
                border: "1px solid rgba(2,136,209,0.3)",
              }}
            >
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, mb: 0.5 }}>
                {countResult.message}
              </Typography>
              <Typography sx={{ fontSize: "1.15rem", fontWeight: 700, color: "#0288d1" }}>
                {Number(countResult.totalCount || 0).toLocaleString("en-IN")} records
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
                Filter: {countResult.filterName}
              </Typography>
            </Box>
          ) : null}

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
            <Typography sx={{ fontSize: "0.85rem" }}>Give data amount</Typography>
            <Switch
              size="small"
              checked={givePartialData}
              onChange={(e) => setGivePartialData(e.target.checked)}
            />
            {givePartialData && (
              <Typography sx={{ fontSize: "0.85rem" }}>
                {" "}
                <TextField
                  size="small" type="number" inputProps={{ min: 0 }}
                  value={recordsPerPage} onChange={(e) => setRecordsPerPage(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "-" || e.key === "e") e.preventDefault(); }}
                  sx={{
                    width: "80px",
                    "& .MuiInputBase-input": { padding: "4px 6px", fontSize: "0.85rem" },
                    "& .MuiOutlinedInput-root": { ...glassInput, height: "30px" },
                  }}
                />
              </Typography>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2 }}>
            <Typography sx={{ fontSize: "0.85rem" }}>Split data in to files</Typography>
            <Switch
              size="small"
              checked={splitData}
              onChange={(e) => setSplitData(e.target.checked)}
            />
            {splitData && (
              <Typography sx={{ fontSize: "0.85rem" }}>
                {" "}
                <TextField
                  size="small" type="number" inputProps={{ min: 0 }}
                  value={batchLimit} onChange={(e) => setBatchLimit(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "-" || e.key === "e") e.preventDefault(); }}
                  sx={{
                    width: "80px",
                    "& .MuiInputBase-input": { padding: "4px 6px", fontSize: "0.85rem" },
                    "& .MuiOutlinedInput-root": { ...glassInput, height: "30px" },
                  }}
                />{" "}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSubmitDialog} color="secondary" sx={{ textTransform: "none", fontSize: "0.8rem" }}>Cancel</Button>
          <Button variant="contained" color="primary"
            sx={{ textTransform: "none", fontSize: "0.8rem", borderRadius: "8px" }}
            onClick={async () => {
              const success = await handleSubmit();
              if (success) setOpenSubmitDialog(false);
            }}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: "0.9rem", fontWeight: 600 }}>Delete Filter</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: "0.85rem" }}>
            Are you sure you want to delete this filter? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} color="secondary" sx={{ textTransform: "none", fontSize: "0.8rem" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={isDeleting}
            sx={{ textTransform: "none", fontSize: "0.8rem", borderRadius: "8px" }}
            onClick={handleConfirmDelete}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Filter Confirmation Dialog */}
      <Dialog open={openCancelDialog} onClose={closeCancelDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: "0.9rem", fontWeight: 600 }}>Cancel Filter</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: "0.85rem" }}>
            {cancelTargetItem?.filtername ? (
              <>Are you sure you want to cancel <b>{cancelTargetItem.filtername}</b>? This will stop the filter while it's pending / in progress.</>
            ) : (
              "Are you sure you want to cancel this filter?"
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCancelDialog} color="secondary" sx={{ textTransform: "none", fontSize: "0.8rem" }}>
            Keep Filter
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={isCancellingFilter}
            sx={{ textTransform: "none", fontSize: "0.8rem", borderRadius: "8px" }}
            onClick={handleConfirmCancel}
          >
            {isCancellingFilter ? "Cancelling..." : "Cancel Filter"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Filter Dialog (Action → Edit) */}
      <Dialog open={openEditDialog} onClose={isUpdatingFilter ? undefined : closeEditDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: "0.9rem", fontWeight: 600 }}>
          Edit Filter — {editTargetItem?.filtername || ""}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontSize: "0.8rem", fontWeight: 600 }}>Filter Name</Typography>
            <TextField
              size="small"
              value={editFilterName}
              onChange={(e) => setEditFilterName(e.target.value)}
              sx={filterNameFieldSx}
            />
          </Box>

          {/* Chip drag-drop area */}
          <Box
            sx={{
              border: "1px solid #E2E8F0",
              borderRadius: 0.5,
              p: 2,
              display: "flex",
              alignItems: "flex-start",
              gap: 3,
              mb: 2,
              background: "rgba(255,255,255,0.5)",
              backdropFilter: "blur(8px)",
            }}
          >
            <Box sx={{ flex: 2, display: "flex", flexDirection: "row", gap: 4 }}>
              <Box>
                <Typography variant="h6" sx={{ mb: 1.8, fontSize: "0.9rem", fontWeight: 600 }}>
                  Add Fields
                </Typography>
                <FormControl size="small">
                  <Select value={editSelected} onChange={handleEditFieldSelect} displayEmpty sx={smallSelectSx}>
                    <MenuItem value="" disabled sx={{ fontSize: "0.8rem" }}>Select option</MenuItem>
                    {fieldList.map((name) => (
                      <MenuItem key={name} value={name} disabled={editChips.includes(name)} sx={{ fontSize: "0.8rem" }}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ mb: 1, fontSize: "0.9rem", fontWeight: 600 }}>
                  Selected Fields Sequence
                </Typography>
                <DragDropContext onDragEnd={onEditDragEnd}>
                  <Droppable droppableId="editChips" direction="horizontal">
                    {(provided) => (
                      <Stack direction="row" spacing={1} sx={chipAreaSx} ref={provided.innerRef} {...provided.droppableProps}>
                        {editChips.map((chip, index) => (
                          <Draggable key={chip} draggableId={chip} index={index}>
                            {(provided) => (
                              <Chip
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                label={chip}
                                onDelete={() => handleEditChipDelete(chip)}
                                variant="outlined"
                                sx={{ borderRadius: "10px", background: "rgba(255,255,255,0.85)", backdropFilter: "blur(4px)", fontSize: "0.82rem", fontWeight: 500 }}
                              />
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </Stack>
                    )}
                  </Droppable>
                </DragDropContext>
              </Box>
            </Box>
          </Box>

          {/* Filter conditions table */}
          <Paper elevation={1} sx={{ borderRadius: 1, overflow: "hidden" }}>
            <TableContainer sx={{ maxHeight: 360 }}>
              <Table stickyHeader size="small" sx={{ minWidth: 700 }}>
                <TableHead sx={{ bgcolor: "#0288d1" }}>
                  <TableRow>
                    <TableCell sx={thCell}>Join</TableCell>
                    <TableCell sx={thCell}>Fields</TableCell>
                    <TableCell sx={thCell}>Operation</TableCell>
                    <TableCell sx={{ ...thCell, borderRight: "none" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        Value
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleEditAddRow}
                          disabled={editSelectedFields.length >= fieldList.length}
                          endIcon={<AddIcon />}
                          sx={{
                            fontSize: "0.72rem",
                            textTransform: "none",
                            color: "white",
                            borderColor: "rgba(255,255,255,0.6)",
                            "&:hover": { borderColor: "white", background: "rgba(255,255,255,0.1)" },
                          }}
                        >
                          Add Filter
                        </Button>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...thCell, borderRight: "none" }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody sx={{ background: "rgba(255,255,255,0.23)" }}>
                  {editSelectedFields.map((field, index) => (
                    <TableRow key={index} hover sx={{ "& td": { fontSize: "0.82rem", py: 1 }, "&:hover": { background: "rgba(186,230,253,0.2)" } }}>
                      <TableCell sx={tdCell}>
                        {index === 0 ? (
                          <Typography sx={{ fontSize: "0.72rem", color: "text.secondary", textAlign: "center" }}>-</Typography>
                        ) : (
                          <FormControl fullWidth size="small" sx={{ minWidth: 90 }}>
                            <Select
                              value={editLogicalOperators[index] || "AND"}
                              onChange={(e) => handleEditLogicalOperatorChange(index, e.target.value)}
                              sx={smallSelectSx}
                              inputProps={{ "aria-label": `Logical operator before filter ${index + 1}` }}
                            >
                              <MenuItem value="AND" sx={{ fontSize: "0.8rem" }}>AND</MenuItem>
                              <MenuItem value="OR" sx={{ fontSize: "0.8rem" }}>OR</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      </TableCell>
                      <TableCell sx={tdCell}>
                        <FormControl fullWidth size="small" sx={{ minWidth: 150 }}>
                          <InputLabel id={`edit-field-label-${index}`} sx={{ fontSize: "0.8rem" }}>Field</InputLabel>
                          <Select
                            labelId={`edit-field-label-${index}`}
                            value={field}
                            label="Field"
                            onChange={(e) => handleEditFieldChange(index, e.target.value)}
                            sx={smallSelectSx}
                          >
                            {columns.map((col) => (
                              <MenuItem
                                key={col.column_name}
                                value={col.column_name}
                                disabled={editSelectedFields.some((f, i) => f === col.column_name && i !== index)}
                                sx={{ fontSize: "0.8rem" }}
                              >
                                {col.column_name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell sx={tdCell}>
                        <FormControl fullWidth size="small">
                          <InputLabel id={`edit-op-label-${index}`} sx={{ fontSize: "0.8rem" }}>Operation</InputLabel>
                          <Select
                            labelId={`edit-op-label-${index}`}
                            value={editOperations[index] || ""}
                            label="Operation"
                            onChange={(e) => handleEditOperationChange(index, e.target.value)}
                            sx={smallSelectSx}
                          >
                            {[
                              { value: "equal", label: "Equal" },
                              { value: "in", label: "In" },
                              { value: "contain", label: "Contain" },
                              { value: "greater_than", label: "Greater than" },
                              { value: "less_than", label: "Less than" },
                              { value: "between", label: "Between" },
                              { value: "start with", label: "Start with" },
                              { value: "end with", label: "End with" },
                              { value: "is", label: "Is" },
                              { value: "before", label: "Days before" },
                              { value: "after", label: "Days after" },
                            ].map(({ value, label }) => (
                              <MenuItem key={value} value={value} sx={{ fontSize: "0.8rem" }}>
                                {label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell sx={{ ...tdCell, borderRight: "0.5px solid #d0d0d0" }}>
                        {["before", "after"].includes(editOperations[index]) ? (
                          <FormControl fullWidth size="small">
                            <Select
                              value={editValues[index] || ""}
                              displayEmpty
                              onChange={(e) => handleEditValueChange(index, e.target.value)}
                              sx={smallSelectSx}
                              inputProps={{ "aria-label": `Days ${editOperations[index]} date` }}
                            >
                              <MenuItem value="" disabled>Select days</MenuItem>
                              {Array.from({ length: 30 }, (_, dayIndex) => dayIndex + 1).map((day) => (
                                <MenuItem key={day} value={day}>{day}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : editOperations[index] === "between" ? (
                          <Box sx={{ display: "flex", gap: 1 }}>
                            {isDateLike(editDataTypes[index]) ? (
                              <>
                                <TextField size="small" fullWidth type="datetime-local" InputLabelProps={{ shrink: true }}
                                  value={editValues[index]?.split("|")[0] || ""}
                                  onChange={(e) => handleEditValueChange(index, `${e.target.value}|${editValues[index]?.split("|")[1] || ""}`)}
                                  sx={{ "& .MuiOutlinedInput-root": glassInput }}
                                />
                                <TextField size="small" fullWidth type="datetime-local" InputLabelProps={{ shrink: true }}
                                  value={editValues[index]?.split("|")[1] || ""}
                                  onChange={(e) => handleEditValueChange(index, `${editValues[index]?.split("|")[0] || ""}|${e.target.value}`)}
                                  sx={{ "& .MuiOutlinedInput-root": glassInput }}
                                />
                              </>
                            ) : (
                              <>
                                <TextField size="small" placeholder="From"
                                  value={editValues[index]?.split("|")[0] || ""}
                                  onChange={(e) => handleEditValueChange(index, `${e.target.value}|${editValues[index]?.split("|")[1] || ""}`)}
                                  sx={{ "& .MuiOutlinedInput-root": glassInput }}
                                />
                                <TextField size="small" placeholder="To"
                                  value={editValues[index]?.split("|")[1] || ""}
                                  onChange={(e) => handleEditValueChange(index, `${editValues[index]?.split("|")[0] || ""}|${e.target.value}`)}
                                  sx={{ "& .MuiOutlinedInput-root": glassInput }}
                                />
                              </>
                            )}
                          </Box>
                        ) : isDateLike(editDataTypes[index]) ? (
                          <TextField size="small" fullWidth type="datetime-local" InputLabelProps={{ shrink: true }}
                            value={editValues[index] || ""}
                            onChange={(e) => handleEditValueChange(index, e.target.value)}
                            sx={{ "& .MuiOutlinedInput-root": glassInput }}
                          />
                        ) : (
                          <TextField size="small" fullWidth placeholder="Enter value"
                            value={editValues[index] || ""}
                            onChange={(e) => handleEditValueChange(index, e.target.value)}
                            sx={{ "& .MuiOutlinedInput-root": glassInput }}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ ...tdCell, borderRight: "none" }} align="center">
                        {editSelectedFields.length > 1 && (
                          <Button color="error" size="small" onClick={() => handleEditRemoveRow(index)}
                            sx={{ fontSize: "0.75rem", textTransform: "none" }}>
                            Remove
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog} color="secondary" sx={{ textTransform: "none", fontSize: "0.8rem" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={isUpdatingFilter}
            sx={{ textTransform: "none", fontSize: "0.8rem", borderRadius: "8px" }}
            onClick={handleSaveEditFilter}
          >
            {isUpdatingFilter ? "Updating..." : "Update Filter"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* SMS / Clicker History Dialog */}
      <Dialog open={infoDialogOpen} onClose={handleCloseInfoDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: "0.9rem", fontWeight: 600 }}>
          History — {infoDialogItem?.filtername || ""}
        </DialogTitle>
        <DialogContent dividers>
          {usageHistoryLoading ? (
            <Typography sx={{ fontSize: "0.85rem", mb: 2 }}>Loading usage history...</Typography>
          ) : usageHistoryError ? (
            <Typography sx={{ fontSize: "0.85rem", color: "error.main", mb: 2 }}>
              Unable to load usage history. Please try again.
            </Typography>
          ) : (
            <>
              <Box sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: "0.84rem", fontWeight: 600 }}>Total Used: {usageHistoryData?.total_used ?? "-"}</Typography>
                <Typography sx={{ fontSize: "0.84rem", fontWeight: 600 }}>SMS Used: {usageHistoryData?.total_sms_used ?? "-"}</Typography>
                <Typography sx={{ fontSize: "0.84rem", fontWeight: 600 }}>Clicker Used: {usageHistoryData?.total_clicker_used ?? "-"}</Typography>
              </Box>

              <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, mb: 1 }}>
                SMS History
              </Typography>

              {usageHistoryData?.sms_history?.length > 0 ? (
                <Table size="small" sx={{ mb: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={thCell}>Campaign ID</TableCell>
                      <TableCell sx={thCell}>Created At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usageHistoryData.sms_history.map((sms, i) => (
                      <TableRow key={`sms-${i}`}>
                        <TableCell sx={tdCell}>{sms.campaign_id || "-"}</TableCell>
                        <TableCell sx={tdCell}>{formatDate(sms.createdat)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography sx={{ fontSize: "0.78rem", color: "text.secondary", mb: 2 }}>
                  No SMS history found.
                </Typography>
              )}

              <Divider sx={{ my: 1.5 }} />

              <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, mb: 1 }}>
                Clicker History
              </Typography>
              {usageHistoryData?.clicker_history?.length > 0 ? (
                <Table size="small" sx={{ mb: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={thCell}>Clicked At</TableCell>
                      <TableCell sx={thCell}>Mobile</TableCell>
                      <TableCell sx={thCell}>Link</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usageHistoryData.clicker_history.map((click, i) => (
                      <TableRow key={`click-${i}`}>
                        <TableCell sx={tdCell}>{formatDate(click.createdat || click.clickedAt || click.clicktime)}</TableCell>
                        <TableCell sx={tdCell}>{click.mobile || "-"}</TableCell>
                        <TableCell sx={tdCell}>{click.link || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }}>
                  No clicker history found.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInfoDialog} color="secondary" sx={{ textTransform: "none", fontSize: "0.8rem" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {false && (
        <>
          {/* Select Flow Dialog (ported from EventCreation) */}
      <Dialog
        open={flowDialogOpen}
        onClose={isSavingFlow ? undefined : closeFlowDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <Box sx={dialogHeaderSx}>
          <Avatar sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "#fff", width: 40, height: 40 }}>
            <PlayCircleOutlineIcon />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.7rem", opacity: 0.85, letterSpacing: "0.04em", textTransform: "uppercase" }}>
              Select Flow
            </Typography>
            <Typography
              sx={{ fontSize: "1rem", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              title={flowDialogItem?.filtername}
            >
              {flowDialogItem?.filtername}
            </Typography>
          </Box>
          <Chip
            label={`ID ${flowDialogItem?.customer_requestid ?? ""}`}
            size="small"
            sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.5)", fontSize: "0.7rem" }}
            variant="outlined"
          />
          <IconButton onClick={closeFlowDialog} size="small" sx={{ color: "#fff", ml: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3, background: "#F4F8FB" }}>
          <Stack spacing={2.5}>
            {/* Destination */}
            <Box sx={dialogSectionSx}>
              <Typography sx={sectionLabelSx}>
                <ForumRoundedIcon sx={{ fontSize: "1rem" }} /> Destination
              </Typography>
              <Stack spacing={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel sx={{ fontSize: "0.8rem" }}>Service</InputLabel>
                  <Select label="Service" value={serviceType} onChange={handleServiceChange} sx={smallSelectSx}>
                    {SERVICE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "0.8rem" }}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {serviceType === "flow" && (
                  <FormControl size="small" fullWidth>
                    <InputLabel sx={{ fontSize: "0.8rem" }}>Flow Name</InputLabel>
                    <Select label="Flow Name" value={selectedFlow} onChange={(e) => setSelectedFlow(e.target.value)} sx={smallSelectSx}>
                      {fetchedFlows.map((f) => (
                        <MenuItem key={f.flow_id} value={f.flow_id} sx={{ fontSize: "0.8rem" }}>{f.flow_name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {serviceType && serviceType !== "flow" && (
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <InputLabel sx={{ fontSize: "0.8rem" }}>Template</InputLabel>
                      <Select label="Template" value={selectedTemplate} onChange={(e) => setSelectedTemplate(e.target.value)} sx={smallSelectSx}>
                        {serviceType === "wa" && waTemplates.map((t) => (
                          <MenuItem key={t.template_id} value={t.template_id} sx={{ fontSize: "0.8rem" }}>{t.name}</MenuItem>
                        ))}
                        {serviceType === "rcs" && rcsTemplates.map((t) => (
                          <MenuItem key={t.id} value={t.id} sx={{ fontSize: "0.8rem" }}>{t.name}</MenuItem>
                        ))}
                        {serviceType === "sms" && smsTemplates.map((t) => (
                          <MenuItem key={t.template_id} value={t.template_id} sx={{ fontSize: "0.8rem" }}>{t.template_name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Tooltip title="Preview template">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => setPreviewOpen(true)}
                          disabled={!selectedTemplate}
                          sx={{
                            border: "1px solid #D6E3EC",
                            borderRadius: "8px",
                            color: BRAND,
                            "&:hover": { background: "#EAF4FB" },
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                )}
              </Stack>
            </Box>

            {/* Schedule */}
            <Box sx={dialogSectionSx}>
              <Typography sx={sectionLabelSx}>
                <ScheduleRoundedIcon sx={{ fontSize: "1rem" }} /> Schedule
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl size="small" fullWidth>
                    <InputLabel sx={{ fontSize: "0.8rem" }}>Run Duration</InputLabel>
                    <Select label="Run Duration" value={runDuration} onChange={(e) => setRunDuration(e.target.value)} sx={smallSelectSx}>
                      {RUN_DURATION_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "0.8rem" }}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Run Time"
                    type="time"
                    size="small"
                    fullWidth
                    value={runTime}
                    onChange={(e) => setRunTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ "& .MuiOutlinedInput-root": glassInput }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    size="small"
                    options={timeZones}
                    value={timeZone}
                    onChange={(e, val) => setTimeZone(val || "Asia/Kolkata")}
                    renderInput={(params) => (
                      <TextField {...params} label="Time Zone" sx={{ "& .MuiOutlinedInput-root": glassInput }} />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Next Run At"
                    type="datetime-local"
                    size="small"
                    fullWidth
                    value={nextRunAt}
                    onChange={(e) => setNextRunAt(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ "& .MuiOutlinedInput-root": glassInput }}
                  />
                </Grid>
                {(runDuration === "daily" || (runDuration === "custom" && customScheduleBy === "day")) && (
                  <Grid item xs={12}>
                    <FormControl size="small" fullWidth>
                      <InputLabel sx={{ fontSize: "0.8rem" }}>Run on Days</InputLabel>
                      <Select
                        multiple
                        label="Run on Days"
                        value={selectedDays}
                        onChange={(e) => {
                          const v = e.target.value;
                          setSelectedDays(typeof v === "string" ? v.split(",") : v);
                        }}
                        renderValue={(vals) => vals.map((d) => d.charAt(0) + d.slice(1).toLowerCase()).join(", ")}
                        sx={smallSelectSx}
                      >
                        {WEEKDAY_OPTIONS.map((day) => (
                          <MenuItem key={day} value={day} sx={{ fontSize: "0.8rem" }}>
                            {day.charAt(0) + day.slice(1).toLowerCase()}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Typography sx={{ fontSize: "0.7rem", color: "text.secondary", mt: 0.5 }}>
                      Leave empty to run every day.
                    </Typography>
                  </Grid>
                )}

                {/* Custom run duration: no. of days + date-wise / day-wise */}
                {runDuration === "custom" && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="No of Days"
                        type="number"
                        size="small"
                        fullWidth
                        value={customNoOfDays}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === "" || (Number(v) >= 0 && Number(v) <= MAX_CUSTOM_DAYS)) {
                            setCustomNoOfDays(v);
                          }
                        }}
                        onKeyDown={(e) => { if (e.key === "-" || e.key === "e") e.preventDefault(); }}
                        inputProps={{ min: 1, max: MAX_CUSTOM_DAYS }}
                        helperText={`Max ${MAX_CUSTOM_DAYS} days`}
                        FormHelperTextProps={{ sx: { fontSize: "0.68rem" } }}
                        sx={{ "& .MuiOutlinedInput-root": glassInput }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl size="small" fullWidth>
                        <InputLabel sx={{ fontSize: "0.8rem" }}>Schedule By</InputLabel>
                        <Select
                          label="Schedule By"
                          value={customScheduleBy}
                          onChange={(e) => setCustomScheduleBy(e.target.value)}
                          sx={smallSelectSx}
                        >
                          {CUSTOM_SCHEDULE_BY_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "0.8rem" }}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {customScheduleBy === "date" && (
                      <Grid item xs={12}>
                        <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, mb: 0.75 }}>
                          Select Dates
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: customDates.length ? 1.25 : 0 }}>
                          <TextField
                            type="date"
                            size="small"
                            value={customDateInput}
                            onChange={(e) => setCustomDateInput(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ "& .MuiOutlinedInput-root": glassInput }}
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleAddCustomDate}
                            disabled={!customDateInput}
                            sx={{ textTransform: "none", fontSize: "0.75rem", borderRadius: "8px" }}
                          >
                            Add Date
                          </Button>
                        </Box>
                        {customDates.length > 0 && (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                            {customDates.map((d) => (
                              <Chip
                                key={d}
                                label={d}
                                size="small"
                                onDelete={() => handleRemoveCustomDate(d)}
                                sx={{ fontSize: "0.72rem", borderRadius: "8px" }}
                              />
                            ))}
                          </Box>
                        )}
                      </Grid>
                    )}
                  </>
                )}

                <Grid item xs={12} sm={6}>
                  <FormControl size="small" fullWidth>
                    <InputLabel sx={{ fontSize: "0.8rem" }}>Campaign Type</InputLabel>
                    <Select
                      label="Campaign Type"
                      value={campaignType}
                      onChange={(e) => setCampaignType(e.target.value)}
                      sx={smallSelectSx}
                    >
                      {CAMPAIGN_TYPE_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "0.8rem" }}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>

            {/* Status */}
            <Box
              sx={{
                ...dialogSectionSx,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                py: 1.5,
              }}
            >
              <Typography sx={{ ...sectionLabelSx, mb: 0 }}>
                <ToggleOnRoundedIcon sx={{ fontSize: "1.1rem" }} /> Status
              </Typography>
              <Stack direction="row" spacing={1.2} alignItems="center">
                <Chip
                  label={eventStatus === "active" ? "Active" : "Paused"}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.72rem",
                    color: eventStatus === "active" ? "#1B7A3D" : "#8A6D00",
                    background: eventStatus === "active" ? "#E4F6E9" : "#FFF4D9",
                  }}
                />
                <Switch
                  checked={eventStatus === "active"}
                  onChange={(e) => setEventStatus(e.target.checked ? "active" : "paused")}
                />
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, background: "#F4F8FB", borderTop: "1px solid #E6ECF1" }}>
          <Button onClick={closeFlowDialog} sx={{ textTransform: "none", fontSize: "0.8rem", color: "#5C6B78" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{
              textTransform: "none",
              fontSize: "0.8rem",
              borderRadius: "10px",
              px: 3,
              boxShadow: "0 6px 16px rgba(2,136,209,0.35)",
              background: `linear-gradient(135deg, ${BRAND} 0%, #01579b 100%)`,
            }}
            onClick={handleSaveFlow}
            disabled={isSavingFlow}
            startIcon={isSavingFlow ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isSavingFlow ? "Saving..." : "Save Flow"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <Box sx={dialogHeaderSx}>
          <Avatar sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "#fff", width: 40, height: 40 }}>
            <VisibilityIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "0.7rem", opacity: 0.85, letterSpacing: "0.04em", textTransform: "uppercase" }}>
              Template Preview
            </Typography>
            <Typography sx={{ fontSize: "1rem", fontWeight: 700 }}>
              {serviceType ? SERVICE_OPTIONS.find((s) => s.value === serviceType)?.label : "Preview"}
            </Typography>
          </Box>
          <IconButton onClick={() => setPreviewOpen(false)} size="small" sx={{ color: "#fff" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3, background: "#F4F8FB" }}>
          <Box sx={{ ...dialogSectionSx, background: "#fff" }}>
            {serviceType === "wa" ? (
              (() => {
                const tpl = waTemplateData?.templates?.find((t) => t.template_id === selectedTemplate);
                return renderWhatsAppPreview(tpl);
              })()
            ) : serviceType === "rcs" ? (
              rcsTemplateView ? (
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: "1rem", mb: 1 }}>
                    {rcsTemplateView.template_name || rcsTemplateView.template_name}
                  </Typography>
                  {(rcsTemplateView.rcs_template_type || rcsTemplateView.template_type) && (
                    <Chip
                      size="small"
                      label={`Category: ${rcsTemplateView.rcs_template_type || rcsTemplateView.template_type}`}
                      sx={{ fontSize: "0.72rem", background: "#EEF6FC", mb: 1.5 }}
                    />
                  )}
                  {/* try to locate an image inside RCS template view */}
                  {(() => {
                    const findImageUrl = (obj) => {
                      if (!obj) return null;
                      if (typeof obj === "string") {
                        if (/https?:\/\/.*\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i.test(obj)) return obj;
                        return null;
                      }
                      if (Array.isArray(obj)) {
                        for (const v of obj) {
                          const found = findImageUrl(v);
                          if (found) return found;
                        }
                        return null;
                      }
                      if (typeof obj === "object") {
                        for (const k of Object.keys(obj)) {
                          const found = findImageUrl(obj[k]);
                          if (found) return found;
                        }
                      }
                      return null;
                    };
                    const img = findImageUrl(rcsTemplateView);
                    return img ? (
                      <Box sx={{ mb: 1.5, borderRadius: 2, overflow: "hidden" }}>
                        <img src={img} alt="rcs" style={{ width: "100%", maxHeight: 220, objectFit: "cover" }} onError={(e) => { e.target.style.display = 'none'; }} />
                      </Box>
                    ) : null;
                  })()}
                  <Box sx={{ borderRadius: 2, background: "#F7F9FB", border: "1px solid #E6ECF1", p: 1.5, maxHeight: 260, overflow: "auto" }}>
                    <pre style={{ whiteSpace: "pre-wrap", fontSize: "0.78rem", margin: 0 }}>{JSON.stringify(rcsTemplateView, null, 2)}</pre>
                  </Box>
                </Box>
              ) : (
                <Typography color="text.secondary">No RCS preview available</Typography>
              )
            ) : serviceType === "sms" ? (
              (() => {
                const st = smsTemplates.find((t) => t.template_id === selectedTemplate);
                return (
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>{st?.template_name || "No SMS template selected"}</Typography>
                    <Chip size="small" label="Category: SMS" sx={{ fontSize: "0.72rem", background: "#EEF6FC", mt: 1 }} />
                  </Box>
                );
              })()
            ) : serviceType === "flow" ? (
              (() => {
                const fObj = (flowsResp?.data || []).find((ff) => String(ff.cf_id || ff.flow_id) === String(selectedFlow)) || fetchedFlows.find((ff) => ff.flow_id === selectedFlow) || null;
                return (
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>{fObj?.flow_name || "No flow selected"}</Typography>
                    {fObj?.category && <Chip size="small" label={`Category: ${fObj.category}`} sx={{ fontSize: "0.72rem", background: "#EEF6FC", mt: 1 }} />}
                  </Box>
                );
              })()
            ) : (
              <Typography color="text.secondary">No preview</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, background: "#F4F8FB", borderTop: "1px solid #E6ECF1" }}>
          <Button
            onClick={() => setPreviewOpen(false)}
            variant="outlined"
            sx={{ textTransform: "none", borderRadius: "10px", fontSize: "0.8rem" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}
    </Box>
  );
}

export default ViewDataFields;